import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { apiService } from '../services/api';
import PlayerPickerModal from '../components/PlayerPickerModal';
import FantasyPointsSummary from '../components/FantasyPointsSummary';

interface BudgetLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  passcode?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

interface Participant {
  id: number;
  user_id: number;
  display_name: string;
  role: string;
  joined_at: string;
  total_points: number;
  rank: number;
}

interface FantasyTeam {
  id: number;
  user_id: number;
  tournament_id: number;
  team_name: string;
  total_credits_used: number;
  transfers_left: number;
  is_locked: boolean;
  players: TeamPlayer[];
}

interface TeamPlayer {
  id: number;
  tournament_player_id: number;
  player_id: string;
  name: string;
  team: string;
  role: string;
  credits: number;
  is_captain: boolean;
  is_vice_captain: boolean;
}

// Role limits (must match backend BUDGET_LEAGUE_CONFIG.ROLE_RESTRICTIONS)
const ROLE_MAX: Record<string, number> = {
  'WK-Batsman': 4,
  'Batsman': 6,
  'Bowler': 6,
  'All-Rounder': 4
};
const ROLE_MIN: Record<string, number> = {
  'WK-Batsman': 1,
  'Batsman': 3,
  'Bowler': 3,
  'All-Rounder': 1
};
const MAX_FOREIGN_PLAYERS = 4;

// Airplane symbol for foreign players (shown next to name)
const ForeignPlayerIcon = () => (
  <span className="text-orange-600 text-sm ml-1 inline-flex shrink-0" title="Foreign player" aria-label="Foreign player">✈</span>
);

interface Match {
  id: number;
  name?: string;
  match_name?: string;
  match_date: string;
  status: string;
  team1?: string;
  team2?: string;
  team1_name?: string;
  team2_name?: string;
  winner?: string;
}

interface Standing {
  rank: number;
  user_id: number;
  display_name: string;
  total_points: number;
  team_id?: number;
  /** Committed boosters only (snapshots with applied_booster_id; updates after match lock). */
  boosters_used?: number;
}

interface MatchPoints {
  match_id: number;
  match_name: string;
  total_points: number;
  captain_points: number;
  vice_captain_points: number;
  booster_points: number;
  player_points: PlayerMatchPoint[];
}

interface PlayerMatchPoint {
  player_id: string;
  name: string;
  role: string;
  points: number;
  is_captain: boolean;
  is_vice_captain: boolean;
  stats: {
    runs?: number;
    wickets?: number;
    catches?: number;
    stumpings?: number;
  };
}

// Helper functions for match status and date formatting
function getCurrentGMTTime(): Date {
  return new Date();
}

function parseMatchDateToGMT(dateString: string): Date | null {
  if (!dateString) {
    return null;
  }

  try {
    if (dateString.includes('T') && dateString.includes('Z')) {
      return new Date(dateString);
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing match date:', error);
    return null;
  }
}

function getMatchStatusLocal(match: any): string {
  if (match.status === 'live') {
    return 'Live';
  }
  
  if (match.status === 'completed' || match.status === 'abandoned') {
    return match.status === 'completed' ? 'Completed' : 'Abandoned';
  }
  
  if (match.match_date) {
    const matchDateGMT = parseMatchDateToGMT(match.match_date);
    if (matchDateGMT) {
      const nowGMT = getCurrentGMTTime();
      if (nowGMT >= matchDateGMT) {
        return 'Started';
      }
    }
  }
  
  return 'Upcoming';
}

/** Returns the match id (string) for the most recent match that has started (Live, or last Completed/Abandoned), or null. */
function getMostRecentMatchId(matches: any[]): string | null {
  if (!matches?.length) return null;
  const liveMatch = matches.find((m: any) => getMatchStatusLocal(m) === 'Live');
  if (liveMatch) return String(liveMatch.id);
  for (let i = matches.length - 1; i >= 0; i--) {
    const s = getMatchStatusLocal(matches[i]);
    if (s === 'Completed' || s === 'Abandoned') return String(matches[i].id);
  }
  return null;
}

function formatMatchDate(dateString: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

function formatMatchName(matchName: string): string {
  if (!matchName) return '';
  
  // Remove tournament name if it appears after a comma (common pattern: "Team1 vs Team2, Match No, Tournament Name")
  // Split by comma and take only the parts before tournament name
  const parts = matchName.split(',').map(p => p.trim());
  
  // Find the part that contains "Match" (e.g., "18th Match")
  const matchIndex = parts.findIndex(p => /Match/i.test(p));
  
  if (matchIndex >= 0) {
    // Return everything up to and including the match number part
    return parts.slice(0, matchIndex + 1).join(', ');
  }
  
  // If no match number found, return first part (team names)
  return parts[0] || matchName;
}

const BudgetLeaguePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get tab from URL parameter or default to 'team'
  const tabFromUrl = searchParams.get('tab') as 'info' | 'team' | 'players' | 'standings' | 'matches' | null;
  const validTabs = ['info', 'team', 'players', 'matches', 'standings'] as const;
  const defaultTab = (tabFromUrl && validTabs.includes(tabFromUrl as any)) ? tabFromUrl : 'team';
  const [activeTab, setActiveTab] = useState<'info' | 'team' | 'players' | 'standings' | 'matches'>(defaultTab);
  const [showSettings, setShowSettings] = useState(false);
  
  const [league, setLeague] = useState<BudgetLeague | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [userTeam, setUserTeam] = useState<FantasyTeam | null>(null);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [matchPoints, setMatchPoints] = useState<MatchPoints[]>([]);
  const [matchStandings, setMatchStandings] = useState<any[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<{ teamId: number; matchId: string | null } | null>(null);
  const [snapshotModalData, setSnapshotModalData] = useState<any | null>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [matchPlayerPoints, setMatchPlayerPoints] = useState<any[]>([]);
  const [loadingPlayerPoints, setLoadingPlayerPoints] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  
  // League editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedLeagueName, setEditedLeagueName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [isEditingMaxParticipants, setIsEditingMaxParticipants] = useState(false);
  const [editedMaxParticipants, setEditedMaxParticipants] = useState<number>(0);
  const [isUpdatingMaxParticipants, setIsUpdatingMaxParticipants] = useState(false);

  // Enhanced My Team Tab states
  const [isTransferMode, setIsTransferMode] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([]);
  const [selectedPlayerToReplace, setSelectedPlayerToReplace] = useState<any>(null);
  // Drop & Add: 11 positions (player or null). No fixed role per slot; validation is rule-driven.
  const [transferModeTeam, setTransferModeTeam] = useState<(TeamPlayer | null)[] | null>(null);
  const [selectedRoleForAdd, setSelectedRoleForAdd] = useState<string | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<any[]>([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  
  // C/VC selection in transfer mode
  const [transferModeCaptain, setTransferModeCaptain] = useState<number | null>(null);
  const [transferModeViceCaptain, setTransferModeViceCaptain] = useState<number | null>(null);
  
  // Store original team snapshot for reset functionality
  const [originalTeamSnapshot, setOriginalTeamSnapshot] = useState<FantasyTeam | null>(null);
  
  // Store last match snapshot to track transfers in progress
  const [lastSnapshotPlayerIds, setLastSnapshotPlayerIds] = useState<number[]>([]);
  // Store full player objects from baseline for transfer details display
  const [baselinePlayers, setBaselinePlayers] = useState<TeamPlayer[]>([]);
  // Ref to store baseline for transfers (persists across async state updates)
  const transferBaselineRef = useRef<number[]>([]);
  // State for collapsible transfer details
  const [isTransferDetailsExpanded, setIsTransferDetailsExpanded] = useState(false);
  
  // Recent match points for My Team tab display
  const [recentMatchPoints, setRecentMatchPoints] = useState<any>(null);
  const [recentMatchInfo, setRecentMatchInfo] = useState<any>(null);
  
  // My Team view: list vs field (persisted)
  const [teamViewMode, setTeamViewMode] = useState<'list' | 'field'>(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('budget_league_team_view');
      if (saved === 'list' || saved === 'field') return saved;
    }
    return 'list';
  });
  const setTeamViewModePersisted = (mode: 'list' | 'field') => {
    setTeamViewMode(mode);
    try { window.localStorage.setItem('budget_league_team_view', mode); } catch (_) {}
  };
  
  // Match Navigation for My Team tab
  const [matchNavigation, setMatchNavigation] = useState<any>(null);
  const [currentViewMatchIndex, setCurrentViewMatchIndex] = useState<number>(0);
  const [viewMatchId, setViewMatchId] = useState<string | null>(null);
  const userChoseMatchIdRef = useRef<string | null>(null); // latest viewed match id (for 30s refresh closure)
  const skipNextLoadMatchNavigationRef = useRef(false); // skip one run after save so effect does not overwrite nextMatchStaging
  const isTransferModeRef = useRef(false); // when true, loadMatchNavigation must not overwrite userTeam.players (user is editing)
  const [viewMatchSnapshot, setViewMatchSnapshot] = useState<any>(null);
  const [viewMatchPoints, setViewMatchPoints] = useState<any>(null);
  const [countdownToNext, setCountdownToNext] = useState<string>('');
  const [countdownToLock, setCountdownToLock] = useState<string | null>(null);
  
  // Smart Transfer System - Staging states
  const [stagingState, setStagingState] = useState<any>(null);
  const [effectiveTransfers, setEffectiveTransfers] = useState<number>(0);
  const [nextMatchStaging, setNextMatchStaging] = useState<any>(null); // Staging for next match
  const [isSavingStaging, setIsSavingStaging] = useState(false);
  // Booster (Budget League): enabled list + modal
  const [enabledBoosters, setEnabledBoosters] = useState<Array<{ id: number; name: string; description?: string; max_usage_per_league?: number }>>([]);
  const [usedBoosterIds, setUsedBoosterIds] = useState<number[]>([]);
  const [showBoosterModal, setShowBoosterModal] = useState(false);
  const [boosterModalSelectedId, setBoosterModalSelectedId] = useState<number | null>(null);
  const [boosterModalTTPlayerId, setBoosterModalTTPlayerId] = useState<number | null>(null);
  const [boosterModalIPPlayerId, setBoosterModalIPPlayerId] = useState<number | null>(null);
  const [isSavingBooster, setIsSavingBooster] = useState(false);
  const [changedPlayers, setChangedPlayers] = useState<Set<number>>(new Set());
  const [recentlyAddedPlayerIds, setRecentlyAddedPlayerIds] = useState<Set<number>>(new Set());
  
  // Team Builder states - Slot-based structure
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);
  const [teamBuilderPlayers, setTeamBuilderPlayers] = useState<any[]>([]);
  const [budgetLeagueForeignPlayerIds, setBudgetLeagueForeignPlayerIds] = useState<string[]>([]);
  const [teamBuilderName, setTeamBuilderName] = useState('');
  const [isLoadingTeamBuilder, setIsLoadingTeamBuilder] = useState(false);
  const [playersSearchQuery, setPlayersSearchQuery] = useState('');
  const [playersRoleFilter, setPlayersRoleFilter] = useState<'all' | 'Batsman' | 'Bowler' | 'All-Rounder' | 'WK-Batsman'>('all');
  const [playersTeamFilter, setPlayersTeamFilter] = useState<string>('all');
  const [playersSortBy, setPlayersSortBy] = useState<'name' | 'team' | 'role' | 'credits' | 'points'>('points');
  const [playersSortOrder, setPlayersSortOrder] = useState<'asc' | 'desc'>('desc');

  const isPlayerForeign = (p: any) => (budgetLeagueForeignPlayerIds || []).includes(String(p?.player_id ?? ''));
  // ForeignPlayerIcon is defined at top level (airplane ✈)

  // Slot-based team structure: { slotId: { type: 'wk'|'batsman'|'all-rounder'|'flex'|'bowler', player: Player | null } }
  const [teamSlots, setTeamSlots] = useState<Record<string, { type: string; player: any | null }>>({
    'wk-1': { type: 'wk', player: null },
    'batsman-1': { type: 'batsman', player: null },
    'batsman-2': { type: 'batsman', player: null },
    'batsman-3': { type: 'batsman', player: null },
    'all-rounder-1': { type: 'all-rounder', player: null },
    'flex-1': { type: 'flex', player: null },
    'flex-2': { type: 'flex', player: null },
    'flex-3': { type: 'flex', player: null },
    'bowler-1': { type: 'bowler', player: null },
    'bowler-2': { type: 'bowler', player: null },
    'bowler-3': { type: 'bowler', player: null }
  });
  
  // Player selection view states
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [slotPlayerSearchQuery, setSlotPlayerSearchQuery] = useState('');
  const [playerTeamFilter, setPlayerTeamFilter] = useState<string>('');
  const [playerMatchFilter, setPlayerMatchFilter] = useState<string>('');
  const [playerRoleFilter, setPlayerRoleFilter] = useState<string>('');
  const [playerListSortBy, setPlayerListSortBy] = useState<'name' | 'credits'>('name');
  const [playerListSortOrder, setPlayerListSortOrder] = useState<'asc' | 'desc'>('asc');

  // Load recent match points for My Team tab
  const loadRecentMatchPoints = async (teamId: number, leagueId: number) => {
    try {
      const data = await apiService.getBudgetLeagueTeamRecentMatchPoints(teamId, leagueId);
      if (data.match && data.points) {
        // Create a map for quick lookup
        const pointsMap: any = {};
        data.points.forEach((p: any) => {
          pointsMap[p.tournament_player_id] = p;
        });
        setRecentMatchPoints(pointsMap);
        setRecentMatchInfo(data.match);
        console.log(`✅ Loaded recent match points from: ${data.match.name}`);
      } else {
        setRecentMatchPoints(null);
        setRecentMatchInfo(null);
      }
    } catch (err) {
      console.error('Error loading recent match points:', err);
      setRecentMatchPoints(null);
      setRecentMatchInfo(null);
    }
  };

  // Load match navigation for team
  const loadMatchNavigation = async () => {
    console.log('🚀 loadMatchNavigation called', { userTeam: !!userTeam, league: !!league, userTeamId: userTeam?.id, leagueId: league?.id });
    if (!userTeam || !league) {
      console.log('⚠️ Cannot load match navigation: missing userTeam or league');
      return;
    }
    try {
      console.log('📡 Fetching match navigation for team', userTeam.id, 'league', league.id);
      const data = await apiService.getTeamMatchNavigation(userTeam.id, league.id);
      console.log('✅ Loaded match navigation:', data);
      
      const newMatchIndex = data.current_match_index;
      const matches = data.matches ?? [];
      const matchesLength = matches.length;
      // Use ref so 30s interval sees latest user choice (avoid stale closure)
      const chosenId = userChoseMatchIdRef.current ?? viewMatchId;
      const userViewingPreviousMatch = chosenId != null && data.next_match != null && chosenId !== data.next_match.match_id;
      const preservedIndexInNewList = matches.findIndex((m: any) => m.match_id === chosenId);
      const preserveView = userViewingPreviousMatch && preservedIndexInNewList >= 0;
      const effectiveViewIndex = preserveView ? preservedIndexInNewList : newMatchIndex;

      setMatchNavigation(data);
      if (preserveView) {
        setCurrentViewMatchIndex(preservedIndexInNewList);
      } else {
        setCurrentViewMatchIndex(newMatchIndex);
        userChoseMatchIdRef.current = null;
      }
      
      // Load staging for next match to show effective transfers AND apply to team if viewing next match
      const nextMatchStagingId = data.next_match?.match_id ?? data.next_match?.id;
      if (nextMatchStagingId && userTeam) {
        try {
          const stagingData = await apiService.getTeamStagingState(userTeam.id, String(nextMatchStagingId));
          if (stagingData.staging && stagingData.staging.staged_players) {
            console.log(`📊 Next match staging: ${stagingData.staging.effective_transfers} effective transfers`);
            setNextMatchStaging(stagingData.staging);
            
            const stagedPlayers = Array.isArray(stagingData.staging.staged_players) 
              ? stagingData.staging.staged_players 
              : JSON.parse(stagingData.staging.staged_players || '[]');
            
            // Always set baseline from staging when we have it (so View Transfer Details can show)
            const lastSnapshotPlayers = Array.isArray(stagingData.staging.last_snapshot_players)
              ? stagingData.staging.last_snapshot_players
              : JSON.parse(stagingData.staging.last_snapshot_players || '[]');
            if (lastSnapshotPlayers.length > 0) {
              const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
              transferBaselineRef.current = baselineIds;
              setLastSnapshotPlayerIds(baselineIds);
              setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
                tournament_player_id: p.tournament_player_id || p.id,
                player_id: p.player_id,
                name: p.name || p.player_name,
                role: p.role,
                team: p.team || p.team_name,
                credits: parseFloat(p.credits || 9)
              })));
              console.log(`✅ Set baseline from staging for transfer details display`);
            }
            
            if (stagedPlayers.length > 0) {
              const nextMatchIndex = data.matches.findIndex((m: any) => m.match_id === data.next_match?.match_id);
              const isViewingNextMatch = nextMatchIndex === effectiveViewIndex;
              const currentMatchId = data.matches[effectiveViewIndex]?.match_id;
              const isCurrentMatchNextMatch = currentMatchId === data.next_match?.match_id;
              // Do not overwrite userTeam.players when user is in transfer mode (editing); they would lose in-progress swaps
              if (!isTransferModeRef.current && (isViewingNextMatch || isCurrentMatchNextMatch)) {
                // Apply staged players to userTeam for display
                const stagedTeamPlayers: TeamPlayer[] = stagedPlayers.map((p: any) => {
                  const tid = Number(p.tournament_player_id || p.id);
                  return {
                    id: tid,
                    tournament_player_id: tid,
                    player_id: p.player_id ?? '',
                    name: (p.name || p.player_name) ?? '',
                    role: p.role ?? '',
                    team: (p.team || p.team_name) ?? '',
                    credits: parseFloat(p.credits || 9),
                    is_captain: p.is_captain || (stagingData.staging.captain_id && tid === stagingData.staging.captain_id),
                    is_vice_captain: p.is_vice_captain || (stagingData.staging.vice_captain_id && tid === stagingData.staging.vice_captain_id)
                  };
                });
                
                setUserTeam({
                  ...userTeam,
                  players: stagedTeamPlayers
                });
                
                console.log(`✅ Applied ${stagedTeamPlayers.length} staged players to team display for next match`);
              }
            }
          }
          // Do not set nextMatchStaging(null) here – preserves staging set by handleSaveTransfers
          // when this runs right after save (effect re-runs on userTeam change)
        } catch (stagingErr) {
          // Preserve existing nextMatchStaging on error (e.g. after save, effect runs and fetch fails)
          console.log('ℹ️ Staging fetch in loadMatchNavigation failed (staging unchanged):', stagingErr);
        }
      }
      // Do not clear nextMatchStaging when nextMatchStagingId missing – keep existing
      
      // Only update view/snapshot when not preserving user's choice (e.g. they went back to a previous match)
      if (!preserveView && data.matches && data.matches[newMatchIndex]) {
        const currentMatchId = data.matches[newMatchIndex].match_id;
        const currentMatchStatus = data.matches[newMatchIndex].status;
        if (currentMatchId !== viewMatchId) {
          console.log(`🔄 Match changed: ${viewMatchId} -> ${currentMatchId} (index: ${newMatchIndex}, status: ${currentMatchStatus})`);
          setViewMatchId(currentMatchId);
          
          const isNextMatch = data.next_match && currentMatchId === data.next_match.match_id;
          const isLiveOrCompleted = currentMatchStatus === 'live' || currentMatchStatus === 'completed' || currentMatchStatus === 'abandoned';
          
          if (isNextMatch && !isLiveOrCompleted) {
            console.log(`📋 Viewing next match (${currentMatchId}), will show staged players if available (no snapshot to load)`);
            setViewMatchSnapshot(null);
            setViewMatchPoints(null);
          } else {
            console.log(`📸 Loading snapshot for match ${currentMatchId} (status: ${currentMatchStatus})`);
            await loadMatchSnapshot(currentMatchId);
          }
        }
      }
    } catch (err) {
      console.error('❌ Error loading match navigation:', err);
    }
  };

  // Load snapshot when match index changes
  useEffect(() => {
    if (matchNavigation && matchNavigation.matches && matchNavigation.matches[currentViewMatchIndex] && userTeam) {
      const matchId = matchNavigation.matches[currentViewMatchIndex].match_id;
      if (matchId && matchId !== viewMatchId) {
        // Check if this is the next match (staging is for this match)
        const isNextMatch = matchNavigation.next_match && matchId === matchNavigation.next_match.match_id;
        
        if (isNextMatch) {
          console.log('🔄 Match index changed to next match; staging comes from schedule (already loaded or set after save)', matchId);
          setViewMatchId(matchId);
          // Do not call loadMatchNavigation here – avoids overwriting nextMatchStaging right after save
        } else {
          console.log('🔄 Match index changed, loading snapshot for:', matchId);
          setViewMatchId(matchId);
          loadMatchSnapshot(matchId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewMatchIndex, viewMatchId]);

  // Navigate to different match
  const handleMatchNavigation = async (direction: 'prev' | 'next') => {
    if (!matchNavigation || !matchNavigation.matches) return;
    
    let newIndex = currentViewMatchIndex;
    if (direction === 'prev' && newIndex > matchNavigation.team_start_match_index) {
      newIndex--;
    } else if (direction === 'next' && newIndex < matchNavigation.matches.length - 1) {
      newIndex++;
    } else {
      return; // Can't navigate further
    }
    
    setCurrentViewMatchIndex(newIndex);
    const matchId = matchNavigation.matches[newIndex].match_id;
    setViewMatchId(matchId);
    userChoseMatchIdRef.current = matchId;
    await loadMatchSnapshot(matchId);
  };

  // Load snapshot and points for a specific match
  const loadMatchSnapshot = async (matchId: string) => {
    if (!userTeam) return;
    try {
      console.log('🔍 Loading snapshot for match:', matchId);
      const snapshotData = await apiService.getBudgetLeagueMatchSnapshot(userTeam.id, matchId);
      const snapshot = snapshotData.snapshot || null;
      console.log('📸 Snapshot data:', snapshot ? { 
        playersCount: snapshot.players?.length, 
        hasPlayers: !!snapshot.players,
        team_total_points: snapshot.team_total_points,
        firstPlayer: snapshot.players?.[0] ? {
          tournament_player_id: snapshot.players[0].tournament_player_id,
          player_id: snapshot.players[0].player_id,
          base_points: snapshot.players[0].base_points,
          total_points: snapshot.players[0].total_points
        } : null,
        allPlayersWithPoints: snapshot.players?.map((p: any) => ({
          id: p.tournament_player_id,
          name: p.name,
          base: p.base_points,
          total: p.total_points
        })) || []
      } : 'null');
      setViewMatchSnapshot(snapshot);
      
      // Extract points from snapshot if available
      if (snapshot && snapshot.players && Array.isArray(snapshot.players) && snapshot.players.length > 0) {
        // Convert snapshot players to points map format
        const pointsMap: any = {};
        snapshot.players.forEach((p: any) => {
          // Use tournament_player_id as the key (this is what userTeam.players uses)
          const playerId = p.tournament_player_id;
          if (playerId) {
            pointsMap[playerId] = {
              tournament_player_id: playerId,
              base_points: p.base_points || 0,
              final_points: p.total_points || p.base_points || 0,
              is_captain: p.is_captain || false,
              is_vice_captain: p.is_vice_captain || false
            };
          } else {
            console.warn('⚠️ Snapshot player missing tournament_player_id:', p);
          }
        });
        console.log('✅ Extracted points map:', Object.keys(pointsMap).length, 'players', 'Keys:', Object.keys(pointsMap).slice(0, 5));
        console.log('✅ Snapshot team_total_points:', snapshot.team_total_points);
        setViewMatchPoints(pointsMap);
      } else {
        console.log('⚠️ No snapshot or no players in snapshot, clearing viewMatchPoints');
        setViewMatchPoints(null);
        if (snapshot?.no_snapshot_for_historical_match) {
          console.log('📋 Backend reported no snapshot for this historical match – will show message instead of current team');
        }
        // If no snapshot, try to load from recent match points (for current match)
        // Check if match has points available (completed, abandoned, etc.)
        const matchStatus = matchNavigation?.matches?.find((m: any) => m.match_id === matchId)?.status;
        // Try to load points for any match that might have points (completed, abandoned, etc.)
        if (matchStatus && matchStatus !== 'scheduled' && matchStatus !== 'live') {
          try {
            const pointsData = await apiService.getBudgetLeagueTeamRecentMatchPoints(userTeam.id, league!.id);
            if (pointsData.match && pointsData.match.id === matchId) {
              const pointsMap: any = {};
              (pointsData.points || []).forEach((p: any) => {
                pointsMap[p.tournament_player_id] = p;
              });
              setViewMatchPoints(pointsMap);
            } else {
              setViewMatchPoints(null);
            }
          } catch (pointsErr) {
            console.error('Error loading recent match points:', pointsErr);
            setViewMatchPoints(null);
          }
        } else {
          setViewMatchPoints(null);
        }
      }
    } catch (err) {
      console.error('Error loading match snapshot:', err);
      setViewMatchSnapshot(null);
      setViewMatchPoints(null);
    }
  };

  // Load staging state for a specific match
  const loadStagingState = async (matchId?: string) => {
    const targetMatchId = matchId || viewMatchId;
    if (!userTeam || !targetMatchId) return;
    try {
      const data = await apiService.getTeamStagingState(userTeam.id, targetMatchId);
      setStagingState(data.staging);
      setEffectiveTransfers(data.staging.effective_transfers);
      console.log(`📊 Loaded staging for match ${targetMatchId}: ${data.staging.effective_transfers} effective transfers`);
    } catch (err) {
      console.error('Error loading staging state:', err);
    }
  };
  
  // Load staging for next match (to show effective transfers)
  const loadNextMatchStaging = async () => {
    if (!userTeam || !matchNavigation?.next_match?.id) return;
    try {
      const data = await apiService.getTeamStagingState(userTeam.id, String(matchNavigation.next_match.match_id ?? matchNavigation.next_match.id));
      if (data.staging && data.staging.effective_transfers > 0) {
        console.log(`📊 Next match staging: ${data.staging.effective_transfers} effective transfers`);
        // Store for display but don't override current staging state
      }
    } catch (err) {
      // No staging is fine - means no transfers made yet
      console.log('ℹ️ No staging for next match (expected if no transfers)');
    }
  };

  // Save transfer changes to staging (always for the NEXT match). Uses C/VC from dropdowns when in transfer mode.
  const handleSaveTransfers = async () => {
    const nextMatchId = matchNavigation?.next_match?.match_id ?? matchNavigation?.next_match?.id;
    const targetMatchId = nextMatchId != null ? String(nextMatchId) : viewMatchId;
    if (!userTeam || !targetMatchId) return;
    // Drop & Add: use transfer team when in transfer mode; otherwise use userTeam.players
    let sourcePlayers = (isTransferMode && transferModeTeam?.length)
      ? transferModeTeam.filter((p): p is TeamPlayer => p != null)
      : userTeam.players;
    if (sourcePlayers.length !== 11) {
      setNotification('Team must have exactly 11 players. Fill all slots before saving.');
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    // Sort by role on submit so payload is canonical (backend validates composition only)
    if (isTransferMode && transferModeTeam?.length) {
      sourcePlayers = [...sourcePlayers].sort((a, b) => getRoleOrderForSort(a.role) - getRoleOrderForSort(b.role));
    }
    // Use dropdown selection for C/VC (transfer mode) when set; otherwise fall back to current team
    const captainId = transferModeCaptain ?? sourcePlayers.find((p: any) => p.is_captain)?.tournament_player_id ?? null;
    const viceCaptainId = transferModeViceCaptain ?? sourcePlayers.find((p: any) => p.is_vice_captain)?.tournament_player_id ?? null;
    if (!captainId || !viceCaptainId) {
      setNotification('Please select both Captain and Vice-Captain before saving');
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    if (nextMatchStaging?.applied_booster_id) {
      const appliedBooster = enabledBoosters.find(b => b.id === nextMatchStaging.applied_booster_id);
      if (appliedBooster?.name === 'Triple Threat' && !nextMatchStaging.triple_threat_player_id) {
        setNotification('Select a player for Triple Threat (3×) before saving.');
        setTimeout(() => setNotification(null), 3000);
        return;
      }
      if (appliedBooster?.name === 'Impact Player' && !nextMatchStaging.impact_player_id) {
        setNotification('Select a player for Impact Player (2×) before saving.');
        setTimeout(() => setNotification(null), 3000);
        return;
      }
    }
    setIsSavingStaging(true);
    try {
      // Build staged 11 with C/VC flags from dropdowns so backend and snapshot get correct captain/VC
      const stagedPlayers = sourcePlayers.map((p: any) => {
        const pid = p.tournament_player_id ?? p.id;
        return {
          ...p,
          is_captain: Number(pid) === Number(captainId),
          is_vice_captain: Number(pid) === Number(viceCaptainId)
        };
      });

      // Retry once on 429 (too many requests) so submit isn't lost
      let lastErr: any;
      const boosterOptions = nextMatchStaging?.applied_booster_id != null ? {
        applied_booster_id: nextMatchStaging.applied_booster_id,
        triple_threat_player_id: nextMatchStaging.triple_threat_player_id ?? null,
        impact_player_id: nextMatchStaging.impact_player_id ?? null
      } : undefined;
      for (let attempt = 0; attempt <= 1; attempt++) {
        try {
          await apiService.updateTeamStagingState(userTeam.id, targetMatchId, stagedPlayers, captainId, viceCaptainId, boosterOptions);
          lastErr = null;
          break;
        } catch (e: any) {
          lastErr = e;
          const isRateLimited = e?.message?.includes('Too many requests') || e?.message?.includes('429');
          if (isRateLimited && attempt === 0) {
            setNotification('Too many requests. Retrying in 4 seconds...');
            await new Promise(r => setTimeout(r, 4000));
            continue;
          }
          throw e;
        }
      }
      if (lastErr) throw lastErr;

      // Update UI from the payload we just sent – no refetch, so nothing can overwrite state
      const snapshotIds = (baselinePlayers?.length ? baselinePlayers : [])
        .map((p: any) => Number(p.tournament_player_id ?? p.id))
        .filter((id: number) => !Number.isNaN(id));
      const stagedIds = stagedPlayers
        .map((p: any) => Number(p.tournament_player_id ?? p.id))
        .filter((id: number) => !Number.isNaN(id));
      const newCount = stagedIds.filter((id: number) => !snapshotIds.includes(id)).length;
      const removedCount = snapshotIds.filter((id: number) => !stagedIds.includes(id)).length;
      const effNum = Math.max(newCount, removedCount);

      const lastList = baselinePlayers?.length ? baselinePlayers : [];
      if (lastList.length > 0) {
        const baselineIds = lastList.map((p: any) => Number(p.tournament_player_id ?? p.id));
        transferBaselineRef.current = baselineIds;
        setLastSnapshotPlayerIds(baselineIds);
      }

      setEffectiveTransfers(effNum);
      setNextMatchStaging((prev: any) => ({
        staged_players: stagedPlayers,
        last_snapshot_players: lastList,
        effective_transfers: effNum,
        captain_id: captainId,
        vice_captain_id: viceCaptainId,
        applied_booster_id: prev?.applied_booster_id ?? null,
        triple_threat_player_id: prev?.triple_threat_player_id ?? null,
        impact_player_id: prev?.impact_player_id ?? null
      }));

      const stagedTeamPlayers: TeamPlayer[] = stagedPlayers.map((p: any) => {
        const tid = Number(p.tournament_player_id ?? p.id);
        return {
          id: tid,
          tournament_player_id: tid,
          player_id: p.player_id ?? '',
          name: p.name ?? p.player_name ?? '',
          role: p.role ?? '',
          team: p.team ?? p.team_name ?? '',
          credits: parseFloat(p.credits ?? 9),
          is_captain: tid === Number(captainId),
          is_vice_captain: tid === Number(viceCaptainId)
        };
      });
      setUserTeam({ ...userTeam, players: stagedTeamPlayers });

      setPendingTransfers([]);
      setChangedPlayers(new Set());
      setTransferModeTeam(null);
      setSelectedRoleForAdd(null);
      setSlotPlayerSearchQuery('');
      setPlayerTeamFilter('');
      setPlayerMatchFilter('');
      setPlayerRoleFilter('');
      setIsTransferMode(false);

      if (matchNavigation?.next_match && matchNavigation?.matches) {
        const nextId = matchNavigation.next_match.match_id ?? matchNavigation.next_match.id;
        const nextIdx = matchNavigation.matches.findIndex((m: any) => (m.match_id ?? m.id) === nextId);
        if (nextId) setViewMatchId(String(nextId));
        if (nextIdx >= 0) setCurrentViewMatchIndex(nextIdx);
      }
      skipNextLoadMatchNavigationRef.current = true;

      setNotification(`✅ Changes saved! Effective transfers: ${effNum}`);
      setTimeout(() => setNotification(null), 3000);
    } catch (err: any) {
      console.error('Error saving staging:', err);
      const message = err?.message || 'Failed to save changes';
      setNotification(message.startsWith('❌') ? message : `❌ ${message}`);
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsSavingStaging(false);
    }
  };

  // Revert all changes to last snapshot
  const handleRevertAllChanges = async () => {
    if (!userTeam || !viewMatchId) return;
    try {
      const data = await apiService.revertTeamStaging(userTeam.id, viewMatchId);
      // Reload team data
      await loadUserTeamData(false);
      setEffectiveTransfers(0);
      setChangedPlayers(new Set());
      setNotification('✅ All changes reverted');
      setTimeout(() => setNotification(null), 2000);
    } catch (err) {
      console.error('Error reverting changes:', err);
      setNotification('❌ Failed to revert changes');
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Undo individual player change
  const handleUndoPlayerChange = (playerId: number) => {
    if (!stagingState) return;
    // Find the player in last snapshot and restore them
    const originalPlayer = stagingState.last_snapshot_players.find((p: any) => p.tournament_player_id === playerId);
    if (originalPlayer && userTeam) {
      // Replace current player with original
      const updatedPlayers = userTeam.players.map((p: any) => 
        p.tournament_player_id === playerId ? originalPlayer : p
      );
      setUserTeam({ ...userTeam, players: updatedPlayers });
      setChangedPlayers(prev => {
        const updated = new Set(prev);
        updated.delete(playerId);
        return updated;
      });
    }
  };

  // Revert one transfer in UI only (from View Transfer Details). User can Save later.
  const handleRevertOneTransfer = (inPlayer: any, outPlayer: any) => {
    if (!userTeam || !inPlayer || !outPlayer) return;
    const inId = Number(inPlayer.tournament_player_id ?? inPlayer.id);
    const outAsTeamPlayer = {
      tournament_player_id: outPlayer.tournament_player_id ?? outPlayer.id,
      player_id: outPlayer.player_id,
      name: outPlayer.name ?? outPlayer.player_name,
      role: outPlayer.role,
      team: outPlayer.team ?? outPlayer.team_name,
      credits: parseFloat(outPlayer.credits ?? 9),
      is_captain: false,
      is_vice_captain: false
    };
    const updatedPlayers = userTeam.players.map((p: any) =>
      Number(p.tournament_player_id ?? p.id) === inId ? outAsTeamPlayer : p
    );
    setUserTeam({ ...userTeam, players: updatedPlayers });
    setChangedPlayers(prev => {
      const next = new Set(prev);
      next.delete(inId);
      return next;
    });
  };

  // Revert one transfer in transfer mode (updates transferModeTeam only). Only in transfer mode.
  const handleRevertOneTransferInTeam = (inPlayer: any, outPlayer: any) => {
    if (!transferModeTeam?.length || !inPlayer || !outPlayer) return;
    const inId = Number(inPlayer.tournament_player_id ?? inPlayer.id);
    const outId = Number(outPlayer.tournament_player_id ?? outPlayer.id);
    const outAsTeamPlayer: TeamPlayer = {
      id: outId,
      tournament_player_id: outId,
      player_id: outPlayer.player_id ?? '',
      name: (outPlayer.name ?? outPlayer.player_name) ?? '',
      role: outPlayer.role ?? '',
      team: (outPlayer.team ?? outPlayer.team_name) ?? '',
      credits: parseFloat(String(outPlayer.credits ?? 9)),
      is_captain: false,
      is_vice_captain: false
    };
    setTransferModeTeam(prev => prev && prev.map(p => p != null && (p.tournament_player_id ?? p.id) === inId ? outAsTeamPlayer : p));
  };

  // Countdown timer effect for match start
  useEffect(() => {
    if (!matchNavigation?.next_match) return;
    
    const interval = setInterval(() => {
      // Show countdown until match start (not lock time)
      const matchStartTime = new Date(matchNavigation.next_match.match_date);
      const now = new Date();
      const diff = matchStartTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdownToNext('Match starting soon!');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdownToNext(`${hours}h ${minutes}m ${seconds}s`);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [matchNavigation]);

  // Countdown timer effect for extended lock time
  useEffect(() => {
    if (!matchNavigation?.next_match?.extended_by_admin || !matchNavigation?.next_match?.effective_lock_time) {
      setCountdownToLock(null);
      return;
    }
    
    const interval = setInterval(() => {
      const lockTime = new Date(matchNavigation.next_match.effective_lock_time);
      const now = new Date();
      const diff = lockTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdownToLock('Transfers locked!');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdownToLock(`${hours}h ${minutes}m ${seconds}s`);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [matchNavigation]);

  // Helper function to get default match ID
  // Priority: Most recent live match > Most recent started match (by schedule) > Most recent completed > Next upcoming
  const getDefaultMatchId = () => {
    if (matches.length === 0) return null;
    
    const now = new Date();
    const withDates = matches.map((m: any) => {
      const raw = m.match_date || (m as any).dateTimeGMT || (m as any).start_time;
      const parsed = raw ? new Date(raw.includes('Z') ? raw : `${raw}Z`) : null;
      return { m, date: parsed };
    });
    
    // Step 1: Prefer most recent live match (handle overlapping live matches - case 4)
    const liveMatches = withDates
      .filter((w: any) => w.m.status === 'live' && w.date)
      .sort((a: any, b: any) => (b.date!.getTime() - a.date!.getTime()));
    
    if (liveMatches.length > 0) {
      return String(liveMatches[0].m.id);
    }
    
    // Step 2: Most recent started match (by schedule) - matches that have passed start time
    // This handles case 3: match 18 complete but result not set, match 19 started -> show match 19
    const startedMatches = withDates
      .filter((w: any) => {
        // Match has started if: date has passed OR status is 'live' or 'started'
        const hasStarted = w.date && w.date < now;
        const isStartedStatus = w.m.status === 'live' || w.m.status === 'started';
        // Exclude completed/abandoned matches (they're handled in step 3)
        const notCompleted = w.m.status !== 'completed' && w.m.status !== 'abandoned';
        return (hasStarted || isStartedStatus) && notCompleted;
      })
      .sort((a: any, b: any) => (b.date!.getTime() - a.date!.getTime()));
    
    if (startedMatches.length > 0) {
      return String(startedMatches[0].m.id);
    }
    
    // Step 3: Most recent completed match
    const completed = withDates
      .filter((w: any) => (w.m.status === 'completed' || w.m.status === 'abandoned') && w.date && w.date < now)
      .sort((a: any, b: any) => (b.date!.getTime() - a.date!.getTime()));
    
    if (completed.length > 0) {
      return String(completed[0].m.id);
    }
    
    // Step 4: Next upcoming match
    const upcoming = withDates
      .filter((w: any) => w.date && w.date >= now)
      .sort((a: any, b: any) => (a.date!.getTime() - b.date!.getTime()));
    
    if (upcoming.length > 0) {
      return String(upcoming[0].m.id);
    }
    
    // Fallback: first match in list
    return String(matches[0].id);
  };

  // Reset to default match when switching to Matches tab
  useEffect(() => {
    if (activeTab === 'matches' && matches.length > 0) {
      const defaultMatchId = getDefaultMatchId();
      if (defaultMatchId) {
        setSelectedMatchId(defaultMatchId);
      }
    }
  }, [activeTab]);

  // Set default match on initial matches load
  useEffect(() => {
    if (matches.length > 0 && !selectedMatchId) {
      const defaultMatchId = getDefaultMatchId();
      if (defaultMatchId) {
        setSelectedMatchId(defaultMatchId);
      }
    }
  }, [matches]);

  // Separated team loading logic for better error handling and retry capability
  const loadUserTeamData = async (saveSnapshot: boolean = false): Promise<FantasyTeam | null> => {
    if (!user?.id || !league) {
      console.log('🚫 Cannot load team: missing user or league data');
      return null;
    }

    try {
      console.log('🔍 Loading user team...');
      console.log('🔍 User ID:', user.id);
      console.log('🔍 Tournament ID:', league.live_tournament_id);
      console.log('🔍 League ID:', league.id);
      
      const teamData = await apiService.getBudgetLeagueTeam(league.live_tournament_id, league.id);
      console.log('🔍 Team data loaded:', teamData);

      if (teamData.foreign_player_ids != null) {
        setBudgetLeagueForeignPlayerIds(Array.isArray(teamData.foreign_player_ids) ? teamData.foreign_player_ids : []);
      }

      if (teamData.team) {
        setUserTeam(teamData.team);
        // Load recent match points for this team
        await loadRecentMatchPoints(teamData.team.id, league.id);
        // Save snapshot when entering transfer mode
        if (saveSnapshot) {
          setOriginalTeamSnapshot(JSON.parse(JSON.stringify(teamData.team)));
        }
        
        // Load staging for next match if match navigation is available
        // This ensures staged transfers persist across page reloads
        if (matchNavigation?.next_match?.id) {
          try {
            const stagingData = await apiService.getTeamStagingState(teamData.team.id, String(matchNavigation.next_match.match_id ?? matchNavigation.next_match.id));
            if (stagingData.staging && stagingData.staging.staged_players) {
              const stagedPlayers = Array.isArray(stagingData.staging.staged_players) 
                ? stagingData.staging.staged_players 
                : JSON.parse(stagingData.staging.staged_players || '[]');
              
              if (stagedPlayers.length > 0) {
                // Check if we're viewing the next match
                const currentMatchId = matchNavigation.matches?.[currentViewMatchIndex]?.match_id;
                const isViewingNextMatch = currentMatchId === matchNavigation.next_match?.match_id;
                
                if (isViewingNextMatch) {
                  // Apply staged players to team for display
                  const stagedTeamPlayers: TeamPlayer[] = stagedPlayers.map((p: any) => {
                    const tid = Number(p.tournament_player_id || p.id);
                    return {
                      id: tid,
                      tournament_player_id: tid,
                      player_id: p.player_id ?? '',
                      name: (p.name || p.player_name) ?? '',
                      role: p.role ?? '',
                      team: (p.team || p.team_name) ?? '',
                      credits: parseFloat(p.credits || 9),
                      is_captain: p.is_captain || (stagingData.staging.captain_id && tid === stagingData.staging.captain_id),
                      is_vice_captain: p.is_vice_captain || (stagingData.staging.vice_captain_id && tid === stagingData.staging.vice_captain_id)
                    };
                  });
                  
                  setUserTeam({
                    ...teamData.team,
                    players: stagedTeamPlayers
                  });
                  
                  // Set baseline for transfer details display
                  const lastSnapshotPlayers = Array.isArray(stagingData.staging.last_snapshot_players)
                    ? stagingData.staging.last_snapshot_players
                    : JSON.parse(stagingData.staging.last_snapshot_players || '[]');
                  
                  if (lastSnapshotPlayers.length > 0) {
                    const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
                    transferBaselineRef.current = baselineIds;
                    setLastSnapshotPlayerIds(baselineIds);
                    setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
                      tournament_player_id: p.tournament_player_id || p.id,
                      player_id: p.player_id,
                      name: p.name || p.player_name,
                      role: p.role,
                      team: p.team || p.team_name,
                      credits: parseFloat(p.credits || 9)
                    })));
                  }
                  
                  console.log(`✅ Loaded and applied ${stagedTeamPlayers.length} staged players from persistence`);
                }
                
                setNextMatchStaging(stagingData.staging);
              }
            }
          } catch (stagingErr) {
            // No staging is fine
            console.log('ℹ️ No staging found on team load (expected if no transfers)');
          }
        }
        
        // Load staging for next match to show effective transfers (if match navigation is loaded)
        // This will be called after matchNavigation loads
        // Store last snapshot player IDs if available from API
        // BUT: Preserve staging baseline if it exists (it's the correct baseline for transfer details)
        setLastSnapshotPlayerIds(prev => {
          // Check if we have a staging baseline set (from transferBaselineRef)
          // Staging baseline takes precedence because it's the baseline before the transfer
          const stagingBaseline = transferBaselineRef.current;
          if (stagingBaseline && stagingBaseline.length > 0) {
            console.log('ℹ️ Preserving staging baseline for transfer details:', stagingBaseline);
            return stagingBaseline;
          }
          
          // If API has valid snapshot data and no staging baseline, use it
          if (teamData.last_snapshot_player_ids && Array.isArray(teamData.last_snapshot_player_ids) && teamData.last_snapshot_player_ids.length > 0) {
            console.log('🔍 Last snapshot player IDs from API:', teamData.last_snapshot_player_ids);
            transferBaselineRef.current = teamData.last_snapshot_player_ids;
            return teamData.last_snapshot_player_ids;
          }
          
          // Check existing state for baseline
          const existingBaseline = prev.length > 0 ? prev : transferBaselineRef.current;
          if (existingBaseline.length > 0) {
            console.log('ℹ️ Preserving existing baseline, API returned empty snapshot:', existingBaseline);
            return existingBaseline;
          }
          
          // Fallback: use current team as snapshot if no snapshot exists and no baseline
          console.log('ℹ️ No snapshot found, using current team as baseline');
          const currentPlayerIds = teamData.team.players
            .map((p: any) => p.tournament_player_id || p.id)
            .filter((id: any) => id != null);
          transferBaselineRef.current = currentPlayerIds;
          return currentPlayerIds;
        });
        return teamData.team;
      } else {
        console.log('ℹ️ No team found for user');
        setUserTeam(null);
        return null;
      }
    } catch (err: any) {
      console.error('❌ Error loading user team:', err);
      
      // Don't show error notification for "no team" scenarios
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        console.log('ℹ️ User has no team yet (expected for new users)');
        setUserTeam(null);
        return null;
      }
      
      // Show error for actual failures
      showNotification(`Failed to load team: ${err.message}`);
      setUserTeam(null);
      return null;
    }
  };

  // Enterprise-level data loading with proper dependency management
  useEffect(() => {
    if (id && user) {
      loadLeagueData();
    }
  }, [id, user]);

  // Separate effect for handling user authentication state changes
  useEffect(() => {
    if (id && user && !userTeam && !loading) {
      // Retry loading team data if user is available but team is not loaded
      console.log('🔄 Retrying team load due to user state change');
      loadUserTeamData();
    }
  }, [id, user, userTeam, loading]);

  // Enterprise-level: Handle user logout by clearing team data
  useEffect(() => {
    if (!user && userTeam) {
      console.log('🔄 User logged out, clearing team data');
      setUserTeam(null);
      setMatchPoints([]);
    }
  }, [user, userTeam]);

  // Keep ref in sync so loadMatchNavigation (async) can avoid overwriting user edits in transfer mode
  useEffect(() => {
    isTransferModeRef.current = isTransferMode;
  }, [isTransferMode]);

  // Load match navigation when team is loaded
  useEffect(() => {
    if (skipNextLoadMatchNavigationRef.current) {
      console.log('⏭️ Skipping loadMatchNavigation (just saved staging)');
      skipNextLoadMatchNavigationRef.current = false;
      return;
    }
    console.log('🔍 Match navigation useEffect triggered', { 
      hasUserTeam: !!userTeam, 
      hasLeague: !!league, 
      activeTab,
      shouldLoad: userTeam && league && activeTab === 'team'
    });
    if (userTeam && league && activeTab === 'team') {
      console.log('✅ Conditions met, calling loadMatchNavigation');
      loadMatchNavigation();
    } else {
      console.log('⚠️ Conditions not met for loadMatchNavigation');
    }
  }, [userTeam, league, activeTab]);

  // Auto-refresh match navigation periodically when on My Team tab to catch new live matches
  useEffect(() => {
    if (!userTeam || !league || activeTab !== 'team') {
      return;
    }

    // Refresh every 60 seconds to catch when new matches go live (reduces rate-limit pressure)
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing match navigation to check for new live matches...');
      loadMatchNavigation();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [userTeam, league, activeTab]);

  // When viewing a live match on My Team, periodically refetch snapshot so per-player and total points update
  const currentMatchForRefresh = matchNavigation?.matches?.[currentViewMatchIndex];
  const isViewingLiveMatch = currentMatchForRefresh?.status === 'live' && viewMatchId && activeTab === 'team';
  useEffect(() => {
    if (!isViewingLiveMatch || !userTeam) return;
    const LIVE_SNAPSHOT_INTERVAL_MS = 90000; // 90 seconds
    const interval = setInterval(() => {
      loadMatchSnapshot(viewMatchId!);
    }, LIVE_SNAPSHOT_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingLiveMatch, viewMatchId, userTeam?.id]);

  // Handle URL parameter changes for tab persistence
  useEffect(() => {
    const rawTabFromUrl = searchParams.get('tab');
    // Handle legacy 'settings' tab by redirecting to 'info'
    if (rawTabFromUrl === 'settings') {
      setSearchParams({ tab: 'info' });
      setActiveTab('info');
      return;
    }
    const tabFromUrl = rawTabFromUrl as 'info' | 'team' | 'players' | 'standings' | 'matches' | null;
    if (tabFromUrl && validTabs.includes(tabFromUrl as any) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    } else if (!tabFromUrl && activeTab !== 'team') {
      setSearchParams({ tab: activeTab });
    }
  }, [searchParams, activeTab, setSearchParams, validTabs]);

  // Reload standings when standings tab is clicked
  useEffect(() => {
    const reloadStandings = async () => {
      if (activeTab === 'standings' && league?.id) {
        try {
          console.log('🔍 Reloading standings for tab change...');
          const standingsData = await apiService.getBudgetLeagueStandings(league.id);
          console.log('🔍 Received standings data:', standingsData);
          console.log('🔍 Standings array:', standingsData.standings);
          if (standingsData.standings && standingsData.standings.length > 0) {
            console.log('🔍 First standing sample:', standingsData.standings[0]);
            console.log('🔍 First standing total_points:', standingsData.standings[0].total_points);
          }
          setStandings(standingsData.standings || []);
        } catch (err) {
          console.error('Failed to reload standings:', err);
        }
      }
    };
    reloadStandings();
  }, [activeTab, league?.id]);

  // Lazy-load players when the Players tab is opened
  useEffect(() => {
    if (activeTab === 'players' && league && teamBuilderPlayers.length === 0) {
      loadTeamBuilderPlayers();
    }
  }, [activeTab, league, teamBuilderPlayers.length]);

  // Single body scroll lock when add-player / team-builder modal is open (PlayerPickerModal receives disableBodyScrollLock so only this effect runs)
  const isPlayerModalOpen = showTeamBuilder || (isTransferMode && showPlayerSelection);
  useEffect(() => {
    if (!isPlayerModalOpen) return;
    const savedScrollX = window.scrollX;
    const savedScrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.width = prevWidth;
      // Defer scroll restore so layout has settled after position:fixed is removed (fixes stuck scroll when closing modal)
      requestAnimationFrame(() => {
        window.scrollTo(savedScrollX ?? 0, savedScrollY ?? 0);
      });
    };
  }, [isPlayerModalOpen]);

  // Load match standings when selected match changes
  useEffect(() => {
    const loadStandings = async () => {
      if (!league || !selectedMatchId) return;
      try {
        const res = await apiService.getBudgetLeagueMatchStandings(league.id, selectedMatchId);
        setMatchStandings(res.standings || []);
      } catch (err: any) {
        console.error('Failed to load match standings:', err);
        setMatchStandings([]);
      }
    };
    loadStandings();
  }, [league, selectedMatchId]);

  // Load match player points when selected match changes
  useEffect(() => {
    const loadPlayerPoints = async () => {
      if (!selectedMatchId || !league) return;
      setLoadingPlayerPoints(true);
      try {
        const res = await apiService.getBudgetLeagueMatchPlayerPoints(selectedMatchId, league.id);
        setMatchPlayerPoints(res.points || []);
      } catch (err: any) {
        console.error('Failed to load match player points:', err);
        setMatchPlayerPoints([]);
      } finally {
        setLoadingPlayerPoints(false);
      }
    };
    loadPlayerPoints();
  }, [selectedMatchId, league]);

  // Load enabled boosters for this tournament (for Booster modal)
  useEffect(() => {
    if (!league?.live_tournament_id) return;
    apiService.getBudgetLeagueEnabledBoosters(league.live_tournament_id)
      .then((r: any) => setEnabledBoosters(Array.isArray(r?.boosters) ? r.boosters : []))
      .catch(() => setEnabledBoosters([]));
  }, [league?.live_tournament_id]);

  // Load used booster IDs for this team (for disabling in Booster modal)
  useEffect(() => {
    if (!userTeam?.id) return;
    apiService.getBudgetLeagueUsedBoosters(userTeam.id)
      .then((r: any) => setUsedBoosterIds(Array.isArray(r?.used_booster_ids) ? r.used_booster_ids : []))
      .catch(() => setUsedBoosterIds([]));
  }, [userTeam?.id]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) {
        setError('League ID is required');
        setLoading(false);
        return;
      }

      const leagueId = parseInt(id);
      if (isNaN(leagueId)) {
        setError('Invalid league ID');
        setLoading(false);
        return;
      }
      
      console.log('🔍 Loading budget league data for league ID:', leagueId);
      
      // Load league details
      console.log('🔍 Loading league details...');
      const leagueData = await apiService.getFantasyLeague(leagueId);
      console.log('🔍 League data loaded:', leagueData);
      setLeague(leagueData.league);
      setIsManager(leagueData.league.created_by === user?.id);
      setCurrentUserId(user?.id || null);
      setEditedLeagueName(leagueData.league.name);
      setEditedMaxParticipants(leagueData.league.max_participants);

      // Load participants
      const participantsData = await apiService.getFantasyLeagueParticipants(leagueId);
      setParticipants(participantsData.participants);

      // Load matches from Budget League endpoint (uses tournament_match_results with synced statuses)
      const matchesData = await apiService.getBudgetLeagueMatches(leagueId);
      const allMatches = matchesData.matches || [];
      setMatches(allMatches);
      
      // Note: Default match will be set by useEffect when needed

      // Load user's team if they're a participant
      let userTeamData = null;
      if (user?.id) {
        userTeamData = await loadUserTeamData();
      }

      // Load standings
      console.log('🔍 Loading standings...');
      const standingsData = await apiService.getBudgetLeagueStandings(leagueId);
      console.log('🔍 Standings data loaded:', standingsData);
      console.log('🔍 Standings array:', standingsData.standings);
      if (standingsData.standings && standingsData.standings.length > 0) {
        console.log('🔍 First standing sample:', standingsData.standings[0]);
        console.log('🔍 First standing total_points:', standingsData.standings[0].total_points);
      }
      setStandings(standingsData.standings || []);

      // Load match points for user's team
      if (userTeamData) {
        try {
          console.log('🔍 Loading match points...');
          const pointsData = await apiService.getFantasyTeamMatchPoints(userTeamData.id);
          console.log('🔍 Match points loaded:', pointsData);
          setMatchPoints(pointsData.matchPoints);
          // Note: Last snapshot player IDs are already loaded from getBudgetLeagueTeam API
        } catch (err) {
          console.log('No match points available yet:', err);
          // Note: Last snapshot player IDs are already loaded from getBudgetLeagueTeam API
        }
      }

    } catch (err: any) {
      console.error('🔍 Error loading league data:', err);
      setError(err.message || 'Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleRenameLeague = async (newName: string) => {
    if (!league || !newName.trim() || newName.trim() === league.name) {
      return;
    }

    setIsRenaming(true);
    try {
      const result = await apiService.updateFantasyLeagueName(league.id, newName.trim());
      if (result.success) {
        setLeague({ ...league, name: newName.trim() });
        showNotification('League name updated successfully!');
      } else {
        showNotification(result.message || 'Failed to update league name');
      }
    } catch (err: any) {
      console.error('Error renaming league:', err);
      showNotification(err.message || 'Failed to update league name');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleUpdateMaxParticipants = async (newMax: number) => {
    if (!league || newMax === league.max_participants || newMax < league.current_participants) {
      return;
    }

    setIsUpdatingMaxParticipants(true);
    try {
      const result = await apiService.updateFantasyLeagueMaxParticipants(league.id, newMax);
      if (result.success) {
        setLeague({ ...league, max_participants: newMax });
        showNotification('Max participants updated successfully!');
      } else {
        showNotification(result.message || 'Failed to update max participants');
      }
    } catch (err: any) {
      console.error('Error updating max participants:', err);
      showNotification(err.message || 'Failed to update max participants');
    } finally {
      setIsUpdatingMaxParticipants(false);
    }
  };

  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${type} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showNotification(`Failed to copy ${type.toLowerCase()}`);
    }
  };

  // Enterprise-level: Manual refresh function for team data
  const handleRefreshTeamData = async () => {
    console.log('🔄 Manual team data refresh requested');
    if (user?.id && league) {
      await loadUserTeamData();
      showNotification('Team data refreshed!');
    } else {
      showNotification('Cannot refresh: User or league data not available');
    }
  };

  const handleCreateTeam = async () => {
    if (!user) {
      showNotification('Please log in to create a team');
      return;
    }
    
    if (!league) {
      showNotification('League data not loaded');
      return;
    }
    
    // Check if user already has a team
    if (userTeam) {
      showNotification('You already have a team for this league');
      return;
    }
    
    // Set team name to user's display name by default
    setTeamBuilderName(user.display_name || '');
    
    // Open team builder modal
    setShowTeamBuilder(true);
    await loadTeamBuilderPlayers();
  };

  const loadTeamBuilderPlayers = async () => {
    if (!league) return;
    
    setIsLoadingTeamBuilder(true);
    try {
      // If on Players tab, load cumulative tournament points
      if (activeTab === 'players') {
        console.log('🔍 Loading tournament points for players tab...');
        try {
          const data = await apiService.getBudgetLeaguePlayerTournamentPoints(league.live_tournament_id);
          console.log('📊 Received player points data:', data);
          if (data.players && data.players.length > 0) {
            setTeamBuilderPlayers(data.players);
            setBudgetLeagueForeignPlayerIds(Array.isArray(data.foreign_player_ids) ? data.foreign_player_ids : []);
            console.log(`✅ Loaded ${data.players.length} players with cumulative points`);
            return;
          }
        } catch (pointsErr: any) {
          console.error('⚠️ Error loading cumulative points, falling back to regular players:', pointsErr);
        }
        
        // Fallback: Load regular players if cumulative points endpoint fails or returns no data
        console.log('ℹ️ Falling back to regular players endpoint...');
      }

      // For team builder, load all available players
      const data = await apiService.getBudgetLeaguePlayers(league.live_tournament_id);
      setBudgetLeagueForeignPlayerIds(Array.isArray(data.foreign_player_ids) ? data.foreign_player_ids : []);
      let players = data.players || [];

      // Fallback: if no players returned from budget-league endpoint, try live tournament players
      if (!players.length) {
        console.log('ℹ️ No players from budget-league endpoint, falling back to live tournament players');
        const fallback = await apiService.getTournamentPlayers(league.live_tournament_id);
        const fallbackPlayers = (fallback.players || fallback.data || []).map((p: any) => ({
          id: p.id || p.player_id,
          tournament_player_id: p.id || p.player_id,
          player_id: p.player_id,
          name: p.name || p.full_name || p.player_name,
          team: p.team || p.team_name,
          role: p.role || p.playing_role || 'Player',
          credits: p.credit_value || p.credits || 9,
          points: p.points || p.fantasy_points || 0
        }));
        players = fallbackPlayers;
      }

      setTeamBuilderPlayers(players);
      
      if (!players.length) {
        showNotification('No players available. Please ensure tournament players are set up in admin panel.');
      }
    } catch (err: any) {
      console.error('❌ Error loading players:', err);
      showNotification(err.message || 'Failed to load players');
      setTeamBuilderPlayers([]);
    } finally {
      setIsLoadingTeamBuilder(false);
    }
  };

  // Helper function to get player points from recent match
  const getPlayerRecentPoints = (tournamentPlayerId: number) => {
    const currentMatch = matchNavigation?.matches?.[currentViewMatchIndex];
    if (!currentMatch) {
      return null;
    }
    // Use snapshot/recent points when available (including for live/scheduled when backend has data)
    if (viewMatchPoints && viewMatchPoints[tournamentPlayerId]) {
      return viewMatchPoints[tournamentPlayerId];
    }
    if (recentMatchPoints && recentMatchInfo && currentMatch.match_id === recentMatchInfo.id) {
      return recentMatchPoints[tournamentPlayerId] || null;
    }
    return null;
  };

  // Helper function to get role icon (returns JSX for batsman image, string for others)
  const getRoleIcon = (role: string): string | React.ReactElement => {
    switch (role) {
      case 'Batsman':
        return (
          <img 
            src="/images/baticon.png" 
            alt="Bat" 
            className="inline-block w-10 h-10 object-contain"
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
        );
      case 'Bowler':
        return '🥎'; // Cricket/softball (ball)
      case 'WK-Batsman':
        return '🧤'; // Wicket keeping gloves
      case 'All-Rounder':
        return '🏏'; // Cricket bat and ball (has both)
      default:
        return '👤';
    }
  };

  // Role order for sorting (WK, BAT, AR, BWL) — shared for list and transfer slots
  const getRoleOrderForSort = (role: string): number => {
    switch (role) {
      case 'Batsman': return 1;
      case 'WK-Batsman': return 2;
      case 'All-Rounder': return 3;
      case 'Bowler': return 4;
      default: return 5;
    }
  };

  // Build (TeamPlayer | null)[] from 11 players. Used when entering transfer mode. No fixed role per slot.
  const buildTransferTeamFromPlayers = (players: any[]): (TeamPlayer | null)[] => {
    const sorted = [...players].sort((a, b) => getRoleOrderForSort(a.role) - getRoleOrderForSort(b.role));
    return sorted.map((p: any) => {
      const tid = Number(p.tournament_player_id ?? p.id);
      return {
        id: tid,
        tournament_player_id: tid,
        player_id: p.player_id ?? '',
        name: (p.name ?? p.player_name) ?? '',
        role: p.role ?? '',
        team: (p.team ?? p.team_name) ?? '',
        credits: parseFloat(String(p.credits ?? 9)),
        is_captain: p.is_captain ?? false,
        is_vice_captain: p.is_vice_captain ?? false
      } as TeamPlayer;
    });
  };

  // Can adding one more player of this role still allow a valid 11? (min/max per role)
  const canAddPlayerForComposition = (filled: TeamPlayer[], newPlayerRole: string): boolean => {
    const count: Record<string, number> = { 'WK-Batsman': 0, 'Batsman': 0, 'All-Rounder': 0, 'Bowler': 0 };
    filled.forEach(p => { count[p.role] = (count[p.role] ?? 0) + 1; });
    count[newPlayerRole] = (count[newPlayerRole] ?? 0) + 1;
    if ((count[newPlayerRole] ?? 0) > (ROLE_MAX[newPlayerRole] ?? 4)) return false;
    const remaining = 11 - filled.length - 1;
    let minNeededSum = 0;
    (['WK-Batsman', 'Batsman', 'All-Rounder', 'Bowler'] as const).forEach(role => {
      const need = Math.max(0, (ROLE_MIN[role] ?? 0) - (count[role] ?? 0));
      minNeededSum += need;
    });
    return remaining >= minNeededSum;
  };

  // Helper function to get unique teams from players
  const getUniqueTeams = () => {
    const teams = Array.from(new Set((teamBuilderPlayers || []).map(player => player.team || '')));
    return teams.filter(Boolean).sort();
  };

  // Helper: available matches in schedule order; upcoming first, started/past at bottom (same as default view logic).
  const getAvailableMatches = () => {
    const now = Date.now();
    const list = matches || [];
    const withMeta = list.map((m: any) => ({
      match: m,
      time: new Date(m.match_date || 0).getTime(),
      isStarted: new Date(m.match_date || 0).getTime() < now || (m.status && m.status !== 'scheduled')
    }));
    // Not-started (upcoming) first in schedule order, then started matches at bottom in schedule order
    withMeta.sort((a, b) => {
      if (a.isStarted !== b.isStarted) return a.isStarted ? 1 : -1;
      return a.time - b.time;
    });
    return withMeta.map(({ match }) => match);
  };

  // Helper function to get filtered players for slot selection.
  // When slotType is wk/batsman/bowler/all-rounder, filter by that role (e.g. field view + opens for one row).
  const getFilteredPlayersForSlot = (slotType: string) => {
    let filtered = [...(teamBuilderPlayers || [])];
    
    const roleBySlot: Record<string, string> = { wk: 'WK-Batsman', batsman: 'Batsman', bowler: 'Bowler', 'all-rounder': 'All-Rounder' };
    const role = slotType ? roleBySlot[slotType] : null;
    if (role) {
      filtered = filtered.filter((p: any) => (p.role || '') === role);
    }
    
    // Exclude already selected players (except the one being replaced)
    // For transfer mode, exclude players already in the team (except the one being replaced)
    if (selectedPlayerToReplace) {
      const currentTeamPlayerIds = (userTeam?.players || [])
        .map((p: any) => p.tournament_player_id || p.id)
        .filter((id: any) => id !== (selectedPlayerToReplace.tournament_player_id || selectedPlayerToReplace.id));
      filtered = filtered.filter(player => !currentTeamPlayerIds.includes(player.id || player.tournament_player_id));
    } else {
      // For team creation, exclude players already in slots
      const selectedPlayerIds = Object.values(teamSlots)
        .filter(slot => slot.player && slot.player.id !== (teamSlots[selectedSlotId || '']?.player?.id))
        .map(slot => slot.player.id);
      filtered = filtered.filter(player => !selectedPlayerIds.includes(player.id));
    }
    
    // Team filter
    if (playerTeamFilter) {
      filtered = filtered.filter(player => player.team === playerTeamFilter);
    }
    
    // Match filter - show players from both teams in the selected match
    if (playerMatchFilter) {
      const match = matches.find(m => m.id.toString() === playerMatchFilter);
      if (match) {
        const matchTeams = [match.team1, match.team2];
        filtered = filtered.filter(player => matchTeams.includes(player.team));
      }
    }
    
    // Search filter
    if (slotPlayerSearchQuery) {
      const query = slotPlayerSearchQuery.toLowerCase();
      filtered = filtered.filter(player => 
        (player.name || '').toLowerCase().includes(query) ||
        (player.team || '').toLowerCase().includes(query)
      );
    }
    
    // Sort: by role (WK → Batsman → All-Rounder → Bowler) then by name for easier scanning
    const roleOrder: Record<string, number> = {
      'WK-Batsman': 0,
      'Batsman': 1,
      'All-Rounder': 2,
      'Bowler': 3
    };
    filtered.sort((a, b) => {
      const orderA = roleOrder[a.role] ?? 4;
      const orderB = roleOrder[b.role] ?? 4;
      if (orderA !== orderB) return orderA - orderB;
      const nameA = (a.name || a.player_name || '').toLowerCase();
      const nameB = (b.name || b.player_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return filtered;
  };

  // Helper function to get slot label
  const getSlotLabel = (slotType: string, slotIndex: number) => {
    const labels: Record<string, string> = {
      'wk': 'Wicket Keeper',
      'batsman': 'Batsman',
      'bowler': 'Bowler',
      'all-rounder': 'All Rounder',
      'flex': 'Flex'
    };
    return labels[slotType] || slotType;
  };

  // Team short name from series_info (match navigation) or fallback
  const getTeamShortName = (teamFullName: string | undefined): string => {
    if (!teamFullName) return '';
    const short = (matchNavigation as any)?.team_short_names?.[teamFullName];
    if (short) return short;
    return teamFullName.slice(0, 3).toUpperCase();
  };

  // Display name: first letter of last name + first name (e.g. "Virat Kohli" → "K. Virat")
  // Display name: first letter of first name + last name (e.g. "Josh Inglis" → "J. Inglis")
  const getPlayerDisplayName = (name: string | undefined): string => {
    if (!name || !name.trim()) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    return `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
  };

  // Avatar initials: last name initial + first name initial (e.g. "Virat Kohli" → "VK" from "K.Virat" style)
  const getPlayerInitials = (name: string | undefined): string => {
    if (!name || !name.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    const first = parts[0].charAt(0).toUpperCase();
    return `${last}${first}`;
  };

  // Helper function to open player selection for a slot
  const handleSlotClick = (slotId: string) => {
    setSelectedSlotId(slotId);
    setShowPlayerSelection(true);
    setSlotPlayerSearchQuery('');
    setPlayerTeamFilter('');
    setPlayerMatchFilter('');
  };

  // Helper function to select a player for a slot
  const handlePlayerSelect = (player: any) => {
    // If this is a transfer (replacing an existing player)
    if (selectedPlayerToReplace) {
      // Add to pending transfers
      const transfer = {
        id: Date.now(),
        out_player: selectedPlayerToReplace,
        in_player: player,
        credit_change: (parseFloat(player.credits) || 0) - (parseFloat(selectedPlayerToReplace.credits) || 0)
      };
      
      // Check budget
      if (!canAddTransfer(player, selectedPlayerToReplace)) {
        const creditChange = transfer.credit_change;
        const projectedBudget = getProjectedBudget() + creditChange;
        showNotification(
          `Transfer would exceed budget limit. Projected budget: ${projectedBudget}/100 credits`
        );
        return;
      }
      
      const outPlayerName = selectedPlayerToReplace.name;
      setPendingTransfers(prev => [...prev, transfer]);
      setSelectedPlayerToReplace(null);
      setShowPlayerSelection(false);
      setSelectedSlotId(null);
      showNotification(`Transfer added: ${outPlayerName} → ${player.name}`);
      // Update team display immediately so IN player is visible in transfer view
      if (userTeam?.players) {
        const outId = selectedPlayerToReplace.tournament_player_id ?? selectedPlayerToReplace.id;
        const inId = player.tournament_player_id ?? player.id;
        const inAsTeamPlayer = {
          tournament_player_id: inId,
          player_id: player.player_id,
          name: player.name ?? player.player_name,
          role: player.role,
          team: player.team ?? player.team_name,
          credits: parseFloat(player.credits ?? 9),
          is_captain: selectedPlayerToReplace?.is_captain ?? false,
          is_vice_captain: selectedPlayerToReplace?.is_vice_captain ?? false
        };
        setUserTeam({
          ...userTeam,
          players: userTeam.players.map((p: any) =>
            (p.tournament_player_id ?? p.id) === outId ? inAsTeamPlayer : p
          )
        });
      }
    } else if (selectedSlotId) {
      // Normal slot assignment (team creation)
      setTeamSlots(prev => ({
        ...prev,
        [selectedSlotId]: {
          ...prev[selectedSlotId],
          player: {
            ...player,
            is_captain: false,
            is_vice_captain: false
          }
        }
      }));
      setShowPlayerSelection(false);
      setSelectedSlotId(null);
    }
  };

  // Helper function to replace a player in a slot
  const handleReplacePlayer = (slotId: string) => {
    handleSlotClick(slotId);
  };

  // Helper function to remove a player from a slot
  const handleRemovePlayer = (slotId: string) => {
    setTeamSlots(prev => ({
      ...prev,
      [slotId]: {
        ...prev[slotId],
        player: null
      }
    }));
  };

  // Helper function to get total credits used
  const getTotalCreditsUsed = () => {
    return Object.values(teamSlots)
      .reduce((total, slot) => {
        if (slot.player && slot.player.credits) {
          return total + parseFloat(slot.player.credits);
        }
        return total;
      }, 0);
  };

  // Helper function to get all selected players
  const getAllSelectedPlayers = () => {
    return Object.values(teamSlots)
      .filter(slot => slot.player !== null)
      .map(slot => slot.player);
  };

  const handleTeamBuilderSubmit = async () => {
    if (!teamBuilderName.trim()) {
      showNotification('Please enter a team name');
      return;
    }
    
    const selectedPlayers = getAllSelectedPlayers();
    if (selectedPlayers.length !== 11) {
      showNotification('Please select exactly 11 players');
      return;
    }
    
    // Check if user already has a team
    if (userTeam) {
      showNotification('You already have a team for this league');
      setShowTeamBuilder(false);
      return;
    }
    
    // Check captain and vice-captain selection
    const captains = selectedPlayers.filter(p => p.is_captain);
    const viceCaptains = selectedPlayers.filter(p => p.is_vice_captain);
    
    if (captains.length !== 1) {
      showNotification('Please select exactly one captain');
      return;
    }
    
    if (viceCaptains.length !== 1) {
      showNotification('Please select exactly one vice-captain');
      return;
    }
    
    try {
      setIsLoadingTeamBuilder(true);
      
      const requestBody = {
        tournamentId: league?.live_tournament_id,
        leagueId: league?.id,
        teamName: teamBuilderName,
        players: selectedPlayers.map(p => {
          // Use tournament_player_id if available, otherwise fall back to id
          const tournamentPlayerId = p.tournament_player_id || p.id;
          if (!tournamentPlayerId) {
            throw new Error(`Player ${p.name || 'Unknown'} is missing tournament_player_id`);
          }
          return {
            tournament_player_id: parseInt(tournamentPlayerId), // Ensure it's a number
            is_captain: p.is_captain || false,
            is_vice_captain: p.is_vice_captain || false
          };
        })
      };
      
      console.log('🔍 Debug - Request Body:', requestBody);
      
      const responseData = await apiService.createBudgetLeagueTeam(requestBody);
      console.log('🔍 Debug - Success Data:', responseData);

      showNotification('Team created successfully!');
      setShowTeamBuilder(false);
      // Reset slots
      setTeamSlots({
        'wk-1': { type: 'wk', player: null },
        'batsman-1': { type: 'batsman', player: null },
        'batsman-2': { type: 'batsman', player: null },
        'batsman-3': { type: 'batsman', player: null },
        'all-rounder-1': { type: 'all-rounder', player: null },
        'flex-1': { type: 'flex', player: null },
        'flex-2': { type: 'flex', player: null },
        'flex-3': { type: 'flex', player: null },
        'bowler-1': { type: 'bowler', player: null },
        'bowler-2': { type: 'bowler', player: null },
        'bowler-3': { type: 'bowler', player: null }
      });
      setTeamBuilderName('');
      
      // Enterprise-level: Refresh team data immediately after creation
      console.log('🔄 Refreshing team data after creation...');
      await loadUserTeamData();
    } catch (err: any) {
      console.log('🔍 Debug - Error:', err);
      showNotification(err.message || 'Failed to create team');
    } finally {
      setIsLoadingTeamBuilder(false);
    }
  };

  const loadSnapshotModal = async (teamId: number, matchId: string | null) => {
    if (!matchId) return;
    setLoadingSnapshot(true);
    try {
      const res = await apiService.getBudgetLeagueMatchSnapshot(teamId, matchId);
      setSnapshotModalData(res.snapshot || null);
    } catch (err: any) {
      console.error('Failed to load snapshot:', err);
      setSnapshotModalData(null);
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const handleMakeTransfers = async () => {
    if (!userTeam || !league) {
      showNotification('You need to create a team first');
      return;
    }
    
    try {
      const nextMatch = matchNavigation?.next_match;
      const stagingMatchId = nextMatch?.match_id ?? nextMatch?.id;
      if (stagingMatchId) {
        try {
          const stagingData = await apiService.getTeamStagingState(userTeam.id, String(stagingMatchId));
          const rawStaged = stagingData.staging?.staged_players;
          const stagedPlayers = Array.isArray(rawStaged) ? rawStaged : (typeof rawStaged === 'string' ? JSON.parse(rawStaged || '[]') : []);
          if (stagedPlayers.length > 0) {
            const stagedTeamPlayers: TeamPlayer[] = stagedPlayers.map((p: any) => {
              const tid = Number(p.tournament_player_id || p.id);
              return {
                id: tid,
                tournament_player_id: tid,
                player_id: p.player_id ?? '',
                name: (p.name || p.player_name) ?? '',
                role: p.role ?? '',
                team: (p.team || p.team_name) ?? '',
                credits: parseFloat(p.credits || 9),
                is_captain: p.is_captain || (stagingData.staging.captain_id && tid === stagingData.staging.captain_id),
                is_vice_captain: p.is_vice_captain || (stagingData.staging.vice_captain_id && tid === stagingData.staging.vice_captain_id)
              };
            });
            setUserTeam({ ...userTeam, players: stagedTeamPlayers });
            const rawLast = stagingData.staging?.last_snapshot_players;
            const lastSnapshotPlayers = Array.isArray(rawLast) ? rawLast : (typeof rawLast === 'string' ? JSON.parse(rawLast || '[]') : []);
            if (lastSnapshotPlayers.length > 0) {
              const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
              transferBaselineRef.current = baselineIds;
              setLastSnapshotPlayerIds(baselineIds);
              setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
                tournament_player_id: p.tournament_player_id || p.id,
                player_id: p.player_id,
                name: p.name || p.player_name,
                role: p.role,
                team: p.team || p.team_name,
                credits: parseFloat(p.credits || 9)
              })));
            }
            setTransferModeCaptain(stagedTeamPlayers.find((p: any) => p.is_captain)?.tournament_player_id || null);
            setTransferModeViceCaptain(stagedTeamPlayers.find((p: any) => p.is_vice_captain)?.tournament_player_id || null);
            setNextMatchStaging(stagingData.staging);
            setTransferModeTeam(buildTransferTeamFromPlayers(stagedTeamPlayers));
            setPendingTransfers([]);
            setIsTransferMode(true);
            return;
          }
        } catch (_) {
          // No staging, fall through to snapshot
        }
      }
      
      console.log('🔄 Loading most recent snapshot for transfer mode...');
      const snapshotData = await apiService.getMostRecentSnapshot(userTeam.id);
      
      if (snapshotData.success && snapshotData.snapshot && snapshotData.snapshot.players) {
        // Use snapshot team for transfers
        const snapshotPlayers = snapshotData.snapshot.players.map((p: any) => ({
          tournament_player_id: p.tournament_player_id || p.id,
          player_id: p.player_id,
          name: p.name || p.player_name,
          role: p.role,
          team: p.team || p.team_name,
          credits: parseFloat(p.credits || 9),
          is_captain: p.is_captain || false,
          is_vice_captain: p.is_vice_captain || false
        }));
        
        // Update userTeam state with snapshot players (temporarily for transfer mode)
        setUserTeam({
          ...userTeam,
          players: snapshotPlayers
        });
        // Set baseline so View Transfer Details works after they make changes and save
        const baselineIds = snapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
        transferBaselineRef.current = baselineIds;
        setLastSnapshotPlayerIds(baselineIds);
        setBaselinePlayers(snapshotPlayers.map((p: any) => ({
          tournament_player_id: p.tournament_player_id || p.id,
          player_id: p.player_id,
          name: p.name || p.player_name,
          role: p.role,
          team: p.team || p.team_name,
          credits: parseFloat(p.credits || 9)
        })));
        
        console.log('✅ Loaded most recent snapshot for transfers:', {
          match_id: snapshotData.snapshot.match_id,
          players_count: snapshotPlayers.length
        });
        
        // Set C/VC from snapshot
        setTransferModeCaptain(snapshotPlayers.find((p: any) => p.is_captain)?.tournament_player_id || null);
        setTransferModeViceCaptain(snapshotPlayers.find((p: any) => p.is_vice_captain)?.tournament_player_id || null);
        setTransferModeTeam(buildTransferTeamFromPlayers(snapshotPlayers));
        setPendingTransfers([]);
      } else {
        // No snapshot exists yet (before first match), use current team
        console.log('⚠️ No snapshot found, using current team for transfers');
        const teamData = await loadUserTeamData(true);
        const currentPlayers = teamData?.players ?? userTeam?.players ?? [];
        setTransferModeCaptain(currentPlayers.find((p: any) => p.is_captain)?.tournament_player_id || null);
        setTransferModeViceCaptain(currentPlayers.find((p: any) => p.is_vice_captain)?.tournament_player_id || null);
        setTransferModeTeam(buildTransferTeamFromPlayers(currentPlayers));
        setPendingTransfers([]);
      }
      
      // Toggle transfer mode
      setIsTransferMode(true);
    } catch (error: any) {
      console.error('❌ Error loading most recent snapshot:', error);
      showNotification('Failed to load team data. Please try again.');
      // Fallback to current team
      await loadUserTeamData(true);
      setTransferModeCaptain(userTeam.players.find(p => p.is_captain)?.tournament_player_id || null);
      setTransferModeViceCaptain(userTeam.players.find(p => p.is_vice_captain)?.tournament_player_id || null);
      setIsTransferMode(true);
    }
  };

  const handleTransferModeToggle = () => {
    if (isTransferMode) {
      // Exiting transfer mode (Cancel): restore team display from saved staging so we don't leave old snapshot visible
      if (nextMatchStaging?.staged_players && userTeam) {
        const raw = nextMatchStaging.staged_players;
        const stagedPlayers = Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
        if (stagedPlayers.length > 0) {
          const stagedTeamPlayers: TeamPlayer[] = stagedPlayers.map((p: any) => {
            const tid = Number(p.tournament_player_id || p.id);
            return {
              id: tid,
              tournament_player_id: tid,
              player_id: p.player_id ?? '',
              name: (p.name || p.player_name) ?? '',
              role: p.role ?? '',
              team: (p.team || p.team_name) ?? '',
              credits: parseFloat(p.credits || 9),
              is_captain: p.is_captain || (nextMatchStaging.captain_id && tid === nextMatchStaging.captain_id),
              is_vice_captain: p.is_vice_captain || (nextMatchStaging.vice_captain_id && tid === nextMatchStaging.vice_captain_id)
            };
          });
          setUserTeam({ ...userTeam, players: stagedTeamPlayers });
          const rawLast = nextMatchStaging.last_snapshot_players;
          const lastSnapshotPlayers = Array.isArray(rawLast) ? rawLast : (typeof rawLast === 'string' ? JSON.parse(rawLast || '[]') : []);
          if (lastSnapshotPlayers.length > 0) {
            const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
            transferBaselineRef.current = baselineIds;
            setLastSnapshotPlayerIds(baselineIds);
            setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
              tournament_player_id: p.tournament_player_id || p.id,
              player_id: p.player_id,
              name: p.name || p.player_name,
              role: p.role,
              team: p.team || p.team_name,
              credits: parseFloat(p.credits || 9)
            })));
          }
        }
      }
      setPendingTransfers([]);
      setSelectedPlayerToReplace(null);
      setTransferModeTeam(null);
      setSelectedRoleForAdd(null);
      setTransferModeCaptain(null);
      setTransferModeViceCaptain(null);
      setSlotPlayerSearchQuery('');
      setPlayerTeamFilter('');
      setPlayerMatchFilter('');
      setPlayerRoleFilter('');
    }
    setIsTransferMode(!isTransferMode);
  };

  const handleResetTeam = async () => {
    if (!userTeam || !league) {
      showNotification('No team to reset');
      return;
    }

    // Get next match ID for staging (API returns next_match with match_id, not id)
    const nextMatch = matchNavigation?.next_match;
    const stagingMatchId = nextMatch?.match_id ?? nextMatch?.id;
    if (!nextMatch || !stagingMatchId) {
      showNotification('No next match found. Cannot reset staging.');
      return;
    }

    if (window.confirm('Are you sure you want to reset your team to the previous snapshot? All saved transfers will be reverted.')) {
      try {
        // Call backend to revert staging to last snapshot
        const result = await apiService.revertTeamStaging(userTeam.id, String(stagingMatchId));
        
        if (result.success) {
          // Reload team data (which will now load from last snapshot since staging is reverted)
          const team = await loadUserTeamData();
          await loadMatchNavigation();
          
          // Reset UI state but preserve C/VC from the reverted (previous snapshot) team.
          // Keep transferModeTeam in sync with reverted team so Drop & Add view shows drop (−) buttons, not Replace.
      setPendingTransfers([]);
      setSelectedPlayerToReplace(null);
      setSelectedRoleForAdd(null);
      if (team?.players?.length) {
            setTransferModeCaptain(team.players.find((p: any) => p.is_captain)?.tournament_player_id ?? null);
            setTransferModeViceCaptain(team.players.find((p: any) => p.is_vice_captain)?.tournament_player_id ?? null);
            setTransferModeTeam(buildTransferTeamFromPlayers(team.players));
          } else {
            setTransferModeTeam(null);
          }
          
          // Update baseline for "Transfers Applied" indicator
          setLastSnapshotPlayerIds([]);
          setBaselinePlayers([]);
          transferBaselineRef.current = [];
          
          // Clear next match staging and effective transfers so transfer count displays correctly
          setNextMatchStaging(null);
          setEffectiveTransfers(0);
          
          showNotification('✅ Team reset to previous snapshot. All saved transfers have been reverted.');
        } else {
          showNotification('Failed to reset team. Please try again.');
        }
      } catch (error: any) {
        console.error('Error resetting team:', error);
        showNotification(error.message || 'Failed to reset team');
      }
    }
  };

  const fetchAvailablePlayers = async (role?: string) => {
    if (!league) return;
    
    console.log('🔍 fetchAvailablePlayers called');
    console.log('🔍 League:', league);
    console.log('🔍 Tournament ID:', league.live_tournament_id);
    console.log('🔍 Role filter:', role);
    
    setIsLoadingPlayers(true);
    try {
      const filters = role ? { role } : undefined;
      console.log('🔍 Making request with filters:', filters);
      
      const data = await apiService.getBudgetLeaguePlayers(league.live_tournament_id, filters);
      console.log('🔍 Response data:', data);
      console.log('🔍 Players count:', data.players?.length || 0);
      
      setAvailablePlayers(data.players || []);
    } catch (err: any) {
      console.error('🔍 Error in fetchAvailablePlayers:', err);
      showNotification(err.message || 'Failed to fetch available players');
      setAvailablePlayers([]);
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  const handlePlayerReplace = async (player: any) => {
    try {
    if (!userTeam?.players?.length) {
      showNotification('Team data is still loading. Please try again.');
      return;
    }
    // Validate player has tournament_player_id
    if (!player.tournament_player_id && !player.id) {
      console.error('❌ Player missing both tournament_player_id and id:', player);
      showNotification('Error: Player data is incomplete. Please refresh the page.');
      return;
    }
    
    // Activate transfer mode if not already active
    if (!isTransferMode) {
      setIsTransferMode(true);
    }
    
    // Determine slot type based on player role
    // For flex players, we need to check if this player could be in a flex slot
    let slotType = 'flex';
    let isFlexPlayer = false;
    
    if (player.role === 'WK-Batsman') {
      slotType = 'wk';
    } else if (player.role === 'Batsman') {
      slotType = 'batsman';
    } else if (player.role === 'Bowler') {
      slotType = 'bowler';
    } else if (player.role === 'All-Rounder') {
      slotType = 'all-rounder';
    }
    
    // Check if this player could be in a flex slot (if there are more players of this role than required)
    if (userTeam?.players) {
      const roleCounts = userTeam.players.reduce((acc: any, p: any) => {
        const role = p.role;
        acc[role] = (acc[role] || 0) + 1;
        return acc;
      }, {});
      
      const requiredCounts: Record<string, number> = {
        'WK-Batsman': 1,
        'Batsman': 3,
        'All-Rounder': 1,
        'Bowler': 3
      };
      
      const requiredCount = requiredCounts[player.role] || 0;
      const actualCount = roleCounts[player.role] || 0;
      
      console.log(`🔍 Checking flex for ${player.name}: role=${player.role}, required=${requiredCount}, actual=${actualCount}`);
      
      // If there are more players of this role than required, some must be in flex slots
      if (actualCount > requiredCount) {
        // Get all players of the same role
        const playersOfSameRole = userTeam.players.filter((p: any) => p.role === player.role);
        
        // Find the index of this specific player in the filtered list
        const playerIndex = playersOfSameRole.findIndex((p: any) => {
          const pId = p.tournament_player_id || p.id;
          const targetId = player.tournament_player_id || player.id;
          return pId === targetId || Number(pId) === Number(targetId);
        });
        
        console.log(`🔍 Player index in same role: ${playerIndex}, required count: ${requiredCount}`);
        
        // If this player is beyond the required count, it's likely in a flex slot
        // For example: if required is 3 Batsman and we have 4, the 4th (index 3) is flex
        // Note: playerIndex is 0-based, so if requiredCount is 3, indices 0,1,2 are required, index 3+ is flex
        if (playerIndex >= requiredCount && playerIndex >= 0) {
          isFlexPlayer = true;
          slotType = 'flex';
          console.log(`✅ FLEX PLAYER DETECTED: ${player.name} (${player.role}) - index ${playerIndex}, required ${requiredCount}, total ${actualCount}`);
        } else {
          console.log(`❌ Not flex: ${player.name} - index ${playerIndex} < required ${requiredCount} (playerIndex=${playerIndex}, requiredCount=${requiredCount})`);
        }
      } else {
        console.log(`❌ Not flex: ${player.name} - actual count ${actualCount} <= required ${requiredCount}`);
      }
    }
    
    // Find a matching slot or create a temporary one for replacement
    const playerId = player.tournament_player_id || player.id;
    // For flex players, always use a flex slot ID so all players are shown
    const matchingSlotId = isFlexPlayer 
      ? 'flex-1' // Use a flex slot for flex players - this ensures getFilteredPlayersForSlot shows all players
      : Object.keys(teamSlots).find(slotId => 
          teamSlots[slotId].type === slotType && (teamSlots[slotId].player?.tournament_player_id === player.tournament_player_id || teamSlots[slotId].player?.id === playerId)
        ) || `temp-replace-${playerId}`;
    
    // Store the player being replaced (ensure tournament_player_id is set)
    const playerToReplace = {
      ...player,
      tournament_player_id: player.tournament_player_id || player.id,
      id: player.tournament_player_id || player.id,
      isFlexPlayer: isFlexPlayer // Store flag to show in modal
    };
    console.log('🔍 Setting selectedPlayerToReplace:', { 
      name: playerToReplace.name, 
      role: playerToReplace.role, 
      isFlexPlayer: playerToReplace.isFlexPlayer,
      playerId: playerToReplace.tournament_player_id || playerToReplace.id
    });
    setSelectedPlayerToReplace(playerToReplace);
    
    // Open player selection modal
    // For flex slots, we'll allow any role (getFilteredPlayersForSlot handles this)
    // IMPORTANT: Set selectedSlotId to a flex slot if isFlexPlayer is true
    setSelectedSlotId(isFlexPlayer ? 'flex-1' : matchingSlotId);
    setShowPlayerSelection(true);
    setSlotPlayerSearchQuery('');
    setPlayerTeamFilter('');
    setPlayerMatchFilter('');
    
    // Load players if not already loaded
    if ((teamBuilderPlayers || []).length === 0) {
      await loadTeamBuilderPlayers();
    }
    } catch (err: any) {
      console.error('Error in handlePlayerReplace:', err);
      showNotification(err?.message || 'Something went wrong. Please try again.');
      setSelectedPlayerToReplace(null);
      setShowPlayerSelection(false);
    }
  };

  const handlePlayerSelection = (newPlayer: any) => {
    if (!selectedPlayerToReplace) return;
    
    console.log('🔍 handlePlayerSelection called');
    console.log('🔍 Selected player to replace:', selectedPlayerToReplace);
    console.log('🔍 New player selected:', newPlayer);
    
    // Ensure both players have tournament_player_id
    const outPlayerId = selectedPlayerToReplace.tournament_player_id || selectedPlayerToReplace.id;
    const inPlayerId = newPlayer.tournament_player_id || newPlayer.id;
    
    if (!outPlayerId) {
      console.error('❌ Out player missing ID:', selectedPlayerToReplace);
      showNotification('Error: Out player data is incomplete.');
      return;
    }
    
    if (!inPlayerId) {
      console.error('❌ In player missing ID:', newPlayer);
      showNotification('Error: In player data is incomplete.');
      return;
    }
    
    // Budget validation
    if (!canAddTransfer(newPlayer, selectedPlayerToReplace)) {
      const creditChange = newPlayer.credits - selectedPlayerToReplace.credits;
      const projectedBudget = getProjectedBudget() + creditChange;
      showNotification(
        `Transfer would exceed budget limit. Projected budget: ${projectedBudget}/100 credits`
      );
      return;
    }
    
    // Add to pending transfers (ensure tournament_player_id is set)
    const transfer = {
      id: Date.now(), // Temporary ID
      out_player: {
        ...selectedPlayerToReplace,
        tournament_player_id: outPlayerId,
        id: outPlayerId
      },
      in_player: {
        ...newPlayer,
        tournament_player_id: inPlayerId,
        id: inPlayerId
      },
      credit_change: newPlayer.credits - selectedPlayerToReplace.credits
    };
    
    console.log('🔍 Adding transfer to pending:', transfer);
    setPendingTransfers(prev => [...prev, transfer]);
    setSelectedPlayerToReplace(null);
    setShowPlayerSelection(false);
    setAvailablePlayers([]);
    setPlayerSearchQuery('');
    showNotification(`Transfer: ${selectedPlayerToReplace.name} → ${newPlayer.name}`);
    
    // Update team display immediately so IN player is visible in transfer view
    if (userTeam?.players) {
      const inAsTeamPlayer = {
        tournament_player_id: inPlayerId,
        player_id: newPlayer.player_id,
        name: newPlayer.name ?? newPlayer.player_name,
        role: newPlayer.role,
        team: newPlayer.team ?? newPlayer.team_name,
        credits: parseFloat(newPlayer.credits ?? 9),
        is_captain: selectedPlayerToReplace?.is_captain ?? false,
        is_vice_captain: selectedPlayerToReplace?.is_vice_captain ?? false
      };
      setUserTeam({
        ...userTeam,
        players: userTeam.players.map((p: any) =>
          (p.tournament_player_id ?? p.id) === outPlayerId ? inAsTeamPlayer : p
        )
      });
    }
  };

  // Drop & Add: remove player from team (by player)
  const handleDropPlayer = (player: TeamPlayer | any) => {
    const playerId = Number(player?.tournament_player_id ?? player?.id);
    if (Number.isNaN(playerId)) return;
    if (showTeamBuilder && !isTransferMode) {
      setTeamSlots(prev => {
        const slotId = Object.keys(prev).find((id) => {
          const p = prev[id]?.player;
          return p && Number(p.tournament_player_id ?? p.id) === playerId;
        });
        if (!slotId) return prev;
        return {
          ...prev,
          [slotId]: {
            ...prev[slotId],
            player: null
          }
        };
      });
      return;
    }
    if (!transferModeTeam?.length) return;
    setTransferModeTeam(prev => {
      if (!prev) return prev;
      const idx = prev.findIndex(p => p != null && Number(p.tournament_player_id ?? p.id) === playerId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = null;
      return next;
    });
  };

  // Drop & Add: open picker. role = which role row (field view); null = list view (any addable role)
  const handleOpenAdd = async (role: string | null = null) => {
    setSelectedRoleForAdd(role);
    setSelectedPlayerToReplace(null);
    setShowPlayerSelection(true);
    setSlotPlayerSearchQuery('');
    if (role != null) setPlayerRoleFilter(role);
    if ((teamBuilderPlayers || []).length === 0) {
      await loadTeamBuilderPlayers();
    }
  };

  // Drop & Add: add selected player to first empty position (picker stays open until XI is full, like H2H)
  const handleAddPlayer = (newPlayer: any) => {
    if (showTeamBuilder && !isTransferMode) {
      const selectedPlayers = getAllSelectedPlayers();
      if (selectedPlayers.length >= 11) return;
      const inId = Number(newPlayer.tournament_player_id ?? newPlayer.id);
      if (selectedPlayers.some((p: any) => Number(p.tournament_player_id ?? p.id) === inId)) return;
      const currentCredits = selectedPlayers.reduce((sum, p: any) => sum + (parseFloat(String(p.credits ?? 0)) || 0), 0);
      const newCredits = parseFloat(String(newPlayer.credits ?? 9)) || 0;
      if (currentCredits + newCredits > 100) return;
      if (!canAddPlayerForComposition(selectedPlayers as any[], newPlayer.role ?? '')) return;
      const foreignIds = budgetLeagueForeignPlayerIds || [];
      const currentForeignCount = selectedPlayers.filter((p: any) => foreignIds.includes(String(p?.player_id ?? ''))).length;
      const isNewPlayerForeign = foreignIds.includes(String(newPlayer?.player_id ?? ''));
      if (isNewPlayerForeign && currentForeignCount >= MAX_FOREIGN_PLAYERS) return;

      const roleToPrefix: Record<string, string> = {
        'WK-Batsman': 'wk-',
        'Batsman': 'batsman-',
        'All-Rounder': 'all-rounder-',
        'Bowler': 'bowler-'
      };
      let wasAdded = false;
      setTeamSlots(prev => {
        const next = { ...prev };
        const tp = {
          ...newPlayer,
          id: inId,
          tournament_player_id: inId,
          name: (newPlayer.name ?? newPlayer.player_name) ?? '',
          team: (newPlayer.team ?? newPlayer.team_name) ?? '',
          credits: newCredits,
          is_captain: false,
          is_vice_captain: false
        };
        const prefix = roleToPrefix[newPlayer.role ?? ''];
        const primarySlot = prefix
          ? Object.keys(next).find((slotId) => slotId.startsWith(prefix) && !next[slotId].player)
          : undefined;
        const fallbackFlex = Object.keys(next).find((slotId) => slotId.startsWith('flex-') && !next[slotId].player);
        const targetSlotId = primarySlot || fallbackFlex;
        if (!targetSlotId) return prev;
        next[targetSlotId] = { ...next[targetSlotId], player: tp };
        wasAdded = true;
        return next;
      });
      const nextLen = selectedPlayers.length + (wasAdded ? 1 : 0);
      if (nextLen >= 11) {
        setShowPlayerSelection(false);
        setSelectedRoleForAdd(null);
      }
      return;
    }
    if (!transferModeTeam?.length) return;
    const filled = transferModeTeam.filter((p): p is TeamPlayer => p != null);
    if (filled.length >= 11) return;
    const currentCredits = filled.reduce((sum, p) => sum + (p?.credits ?? 0), 0);
    const newCredits = parseFloat(newPlayer.credits ?? 9);
    if (currentCredits + newCredits > 100) return;
    const inId = Number(newPlayer.tournament_player_id ?? newPlayer.id);
    if (filled.some(p => (p.tournament_player_id ?? p.id) === inId)) return;
    const tp: TeamPlayer = {
      id: inId,
      tournament_player_id: inId,
      player_id: newPlayer.player_id ?? '',
      name: (newPlayer.name ?? newPlayer.player_name) ?? '',
      role: newPlayer.role ?? '',
      team: (newPlayer.team ?? newPlayer.team_name) ?? '',
      credits: newCredits,
      is_captain: false,
      is_vice_captain: false
    };
    const nextLen = filled.length + 1;
    setTransferModeTeam(prev => {
      if (!prev) return prev;
      const next = [...prev];
      const idx = next.findIndex(p => p == null);
      if (idx < 0) return prev;
      next[idx] = tp;
      return next;
    });
    if (nextLen >= 11) {
      setShowPlayerSelection(false);
      setSelectedRoleForAdd(null);
    }
  };

  const handleRemoveTransfer = (transferId: number) => {
    const transfer = pendingTransfers.find(t => t.id === transferId);
    setPendingTransfers(prev => prev.filter(t => t.id !== transferId));
    if (transfer && userTeam?.players) {
      const outAsTeamPlayer = {
        tournament_player_id: transfer.out_player.tournament_player_id ?? transfer.out_player.id,
        player_id: transfer.out_player.player_id,
        name: transfer.out_player.name ?? transfer.out_player.player_name,
        role: transfer.out_player.role,
        team: transfer.out_player.team ?? transfer.out_player.team_name,
        credits: parseFloat(transfer.out_player.credits ?? 9),
        is_captain: transfer.out_player.is_captain ?? false,
        is_vice_captain: transfer.out_player.is_vice_captain ?? false
      };
      const inId = transfer.in_player.tournament_player_id ?? transfer.in_player.id;
      setUserTeam({
        ...userTeam,
        players: userTeam.players.map((p: any) =>
          (p.tournament_player_id ?? p.id) === inId ? outAsTeamPlayer : p
        )
      });
    }
  };

  // Budget validation functions
  const calculateTotalBudgetImpact = () => {
    return pendingTransfers.reduce((total, transfer) => total + transfer.credit_change, 0);
  };

  const getProjectedBudget = () => {
    if (isTransferMode && transferModeTeam?.length) {
      return transferModeTeam.reduce((sum, p) => sum + (Number(p?.credits) || 0), 0);
    }
    if (!userTeam) return 0;
    return userTeam.total_credits_used + calculateTotalBudgetImpact();
  };

  const isBudgetValid = () => {
    const projectedBudget = getProjectedBudget();
    return projectedBudget <= 100 && projectedBudget >= 0;
  };

  const canAddTransfer = (newPlayer: any, oldPlayer: any) => {
    const creditChange = (parseFloat(newPlayer.credits) || 0) - (parseFloat(oldPlayer.credits) || 0);
    const projectedBudget = getProjectedBudget() + creditChange;
    return projectedBudget <= 100 && projectedBudget >= 0;
  };

  // Captain/VC selection functions - Note: Captain/VC changes are handled through transfers
  // const handleMakeCaptain = async (playerId: number) => {
  //   // This functionality is handled through the transfer system
  //   // where is_captain and is_vice_captain flags are set during transfers
  // };

  // const handleMakeViceCaptain = async (playerId: number) => {
  //   // This functionality is handled through the transfer system
  //   // where is_captain and is_vice_captain flags are set during transfers
  // };

  const handleConfirmTransfers = async () => {
    if (!userTeam) {
      showNotification('Team not found');
      return;
    }
    
    // Store team state before transfer for comparison (both IDs and full objects)
    const teamBeforeTransfer = userTeam.players
      .map(p => p.tournament_player_id || p.id)
      .filter(id => id != null)
      .map(id => Number(id));
    const teamBeforeTransferPlayers = [...userTeam.players]; // Store full player objects
    console.log('🔍 Team before transfer (baseline):', teamBeforeTransfer);

    // Validate C/VC selection in transfer mode
    if (!transferModeCaptain || !transferModeViceCaptain) {
      showNotification('Please select both Captain and Vice-Captain before confirming');
      return;
    }

    if (transferModeCaptain === transferModeViceCaptain) {
      showNotification('Captain and Vice-Captain must be different players');
      return;
    }

    // Check if C/VC has changed (even if no transfers)
    const currentCaptain = userTeam.players.find(p => p.is_captain)?.tournament_player_id || userTeam.players.find(p => p.is_captain)?.id;
    const currentViceCaptain = userTeam.players.find(p => p.is_vice_captain)?.tournament_player_id || userTeam.players.find(p => p.is_vice_captain)?.id;
    const cvcChanged = currentCaptain !== transferModeCaptain || currentViceCaptain !== transferModeViceCaptain;

    // If no transfers and no C/VC change, nothing to do
    if (pendingTransfers.length === 0 && !cvcChanged) {
      showNotification('No changes to save');
      return;
    }

    try {
      // Calculate final team after transfers
      const finalTeamPlayers = userTeam.players.map(player => {
        const playerId = player.tournament_player_id || player.id;
        const transfer = pendingTransfers.find(t => 
          (t.out_player.tournament_player_id || t.out_player.id) === playerId
        );
        if (transfer) {
          return {
            ...transfer.in_player,
            tournament_player_id: transfer.in_player.tournament_player_id || transfer.in_player.id
          };
        }
        return player;
      });

      // Guard: ensure final team has exactly 11 players
      if (finalTeamPlayers.length !== 11) {
        showNotification('Team must have exactly 11 players after transfers');
        return;
      }

      // Guard: ensure selected C/VC are in the final team
      const finalIds = finalTeamPlayers.map(p => p.tournament_player_id || p.id);
      if (!finalIds.includes(transferModeCaptain) || !finalIds.includes(transferModeViceCaptain)) {
        showNotification('Captain and Vice-Captain must be part of your final team');
        return;
      }

      // Format transfers for backend API with validation (if any)
      const transfers = pendingTransfers.map((transfer, index) => {
        const outPlayerId = transfer.out_player.tournament_player_id || transfer.out_player.id;
        const inPlayerId = transfer.in_player.tournament_player_id || transfer.in_player.id;
        
        if (!outPlayerId) {
          console.error('❌ Transfer error - out_player missing ID:', transfer.out_player);
          throw new Error(`Transfer ${index + 1}: Out player is missing tournament_player_id or id`);
        }
        
        if (!inPlayerId) {
          console.error('❌ Transfer error - in_player missing ID:', transfer.in_player);
          throw new Error(`Transfer ${index + 1}: In player is missing tournament_player_id or id`);
        }
        
        return {
          out_player_id: outPlayerId,
          in_player_id: inPlayerId,
          is_captain: false, // C/VC will be set separately for all players
          is_vice_captain: false
        };
      });

      console.log('🔍 Sending to backend:', {
        transfers: transfers.length,
        captain_id: transferModeCaptain,
        vice_captain_id: transferModeViceCaptain
      });

      // Call backend API to process transfers (can be empty) with C/VC info
      const response = await apiService.makeBudgetLeagueTransfers({
        tournamentId: league!.live_tournament_id,
        leagueId: league!.id,
        transfers: transfers, // Can be empty array if only updating C/VC
        captain_id: transferModeCaptain,
        vice_captain_id: transferModeViceCaptain
      });

      if (response.success) {
        // Update transfers_left immediately from response
        const updatedTransfersLeft = response.transfers_left !== undefined ? response.transfers_left : userTeam.transfers_left;
        console.log('🔍 Transfer response:', {
          transfers_left: response.transfers_left,
          transfers_counted: response.transfers_counted,
          transfers_applied: response.transfers_applied,
          current_userTeam_transfers: userTeam.transfers_left,
          updated: updatedTransfersLeft
        });
        
        // Update matchNavigation state immediately
        if (matchNavigation) {
          setMatchNavigation({
            ...matchNavigation,
            transfers_left: updatedTransfersLeft
          });
        }
        
        // Show success message with transfer details
        if (pendingTransfers.length > 0) {
          const transferDetails = pendingTransfers.map(t => 
            `${t.out_player.name || t.out_player.player_name} → ${t.in_player.name || t.in_player.player_name}`
          ).join(', ');
          showNotification(`✅ ${pendingTransfers.length} transfer(s) completed! ${transferDetails}. Transfers left: ${updatedTransfersLeft}`);
          const addedIds = new Set(pendingTransfers.map((t: any) => Number(t.in_player.tournament_player_id ?? t.in_player.id)).filter((id: number) => !isNaN(id)));
          setRecentlyAddedPlayerIds(addedIds);
          setTimeout(() => setRecentlyAddedPlayerIds(new Set()), 4000);
        } else {
          showNotification('✅ Captain and Vice-Captain updated successfully!');
        }
        
        // Reset transfer mode
        setIsTransferMode(false);
        setPendingTransfers([]);
        setSelectedPlayerToReplace(null);
        setTransferModeCaptain(null);
        setTransferModeViceCaptain(null);
        
        // Reload match navigation to get updated staging
        if (league) {
          console.log('🔄 Reloading match navigation to update staging...');
          await loadMatchNavigation();
          
          // After reloading, check if we're viewing the next match
          // If yes, update userTeam with staged players immediately
          // If no (viewing live/completed match), keep showing snapshot
          const updatedMatchNav = await apiService.getTeamMatchNavigation(userTeam.id, league.id);
          const nextMatchId = updatedMatchNav.next_match?.match_id ?? updatedMatchNav.next_match?.id;
          if (updatedMatchNav.success && nextMatchId) {
            const currentMatchId = viewMatchId || matchNavigation?.matches?.[currentViewMatchIndex]?.match_id;
            const isViewingNextMatch = currentMatchId === updatedMatchNav.next_match.match_id;
            
            if (isViewingNextMatch) {
              // Viewing next match - load staging and update UI immediately
              try {
                const stagingData = await apiService.getTeamStagingState(userTeam.id, String(nextMatchId));
                if (stagingData.staging && stagingData.staging.staged_players) {
                  const stagedPlayers = Array.isArray(stagingData.staging.staged_players)
                    ? stagingData.staging.staged_players
                    : JSON.parse(stagingData.staging.staged_players || '[]');
                  
                  if (stagedPlayers.length > 0) {
                    // Update C/VC flags
                    const stagedTeamPlayers = stagedPlayers.map((p: any) => ({
                      tournament_player_id: p.tournament_player_id || p.id,
                      player_id: p.player_id,
                      name: p.name || p.player_name,
                      role: p.role,
                      team: p.team || p.team_name,
                      credits: parseFloat(p.credits || 9),
                      is_captain: p.is_captain || (stagingData.staging.captain_id && (p.tournament_player_id || p.id) === stagingData.staging.captain_id),
                      is_vice_captain: p.is_vice_captain || (stagingData.staging.vice_captain_id && (p.tournament_player_id || p.id) === stagingData.staging.vice_captain_id)
                    }));
                    
                    setUserTeam({
                      ...userTeam,
                      players: stagedTeamPlayers,
                      transfers_left: updatedTransfersLeft
                    });
                    
                    // Set baseline for transfer details
                    const lastSnapshotPlayers = Array.isArray(stagingData.staging.last_snapshot_players)
                      ? stagingData.staging.last_snapshot_players
                      : JSON.parse(stagingData.staging.last_snapshot_players || '[]');
                    
                    if (lastSnapshotPlayers.length > 0) {
                      const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
                      transferBaselineRef.current = baselineIds;
                      setLastSnapshotPlayerIds(baselineIds);
                      setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
                        tournament_player_id: p.tournament_player_id || p.id,
                        player_id: p.player_id,
                        name: p.name || p.player_name,
                        role: p.role,
                        team: p.team || p.team_name,
                        credits: parseFloat(p.credits || 9)
                      })));
                    }
                    
                    console.log('✅ Updated UI with staged players for next match immediately');
                  }
                }
              } catch (err) {
                console.log('ℹ️ Could not load staging (non-fatal):', err);
              }
            } else {
              // Viewing live/completed match - just update transfers_left, keep showing snapshot
              setUserTeam({
                ...userTeam,
                transfers_left: updatedTransfersLeft
              });
              console.log('ℹ️ Viewing live/completed match, keeping snapshot view (staged players will show when viewing next match)');
              
              // Even when not viewing next match, try to load staging to get baseline for transfer details
              if (updatedMatchNav.next_match?.id) {
                try {
                  const stagingData = await apiService.getTeamStagingState(userTeam.id, String(updatedMatchNav.next_match.match_id ?? updatedMatchNav.next_match.id));
                  if (stagingData.staging && stagingData.staging.last_snapshot_players) {
                    const lastSnapshotPlayers = Array.isArray(stagingData.staging.last_snapshot_players)
                      ? stagingData.staging.last_snapshot_players
                      : JSON.parse(stagingData.staging.last_snapshot_players || '[]');
                    
                    if (lastSnapshotPlayers.length > 0) {
                      const baselineIds = lastSnapshotPlayers.map((p: any) => Number(p.tournament_player_id || p.id));
                      transferBaselineRef.current = baselineIds;
                      setLastSnapshotPlayerIds(baselineIds);
                      setBaselinePlayers(lastSnapshotPlayers.map((p: any) => ({
                        tournament_player_id: p.tournament_player_id || p.id,
                        player_id: p.player_id,
                        name: p.name || p.player_name,
                        role: p.role,
                        team: p.team || p.team_name,
                        credits: parseFloat(p.credits || 9)
                      })));
                      console.log('✅ Set baseline from staging for transfer details (not viewing next match)');
                    }
                  }
                } catch (err) {
                  console.log('ℹ️ Could not load staging for baseline (non-fatal):', err);
                }
              }
            }
          }
          
          // Reload team data to get updated last_snapshot_player_ids from backend
          // But preserve staging baseline if it was set above (loadUserTeamData will preserve it if API returns empty)
          console.log('🔄 Reloading team data to update last_snapshot_player_ids...');
          await loadUserTeamData();
        }
        
        // Force a re-render to show updated transfer counts
        console.log('🔄 Transfer completed, UI updated with new players');
      } else {
        showNotification(response.message || 'Failed to complete updates');
      }
    } catch (err: any) {
      console.error('Transfer error:', err);
      showNotification(err.message || 'Failed to complete transfers');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Error Loading League</h2>
          <p className="text-red-300 mb-4">{error}</p>
          <button
            onClick={() => navigate('/budget-league')}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
          >
            Back to Budget Leagues
          </button>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-yellow-400 mb-4">League Not Found</h2>
          <p className="text-yellow-300 mb-4">The requested league could not be found.</p>
          <button
            onClick={() => navigate('/my-leagues')}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg"
          >
            Back to My Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-2 sm:py-6 px-0 sm:px-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-2 sm:p-4 md:p-5 mb-3 sm:mb-6">
        <div className="w-full px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/my-leagues')}
                className="text-blue-100 hover:text-white transition-colors duration-200 mb-1 sm:mb-2 flex items-center text-xs sm:text-sm"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back to Leagues</span>
                <span className="sm:hidden">Back</span>
              </button>
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-accent mb-1 truncate">{league.name}</h1>
              <p className="text-blue-100 text-xs sm:text-sm md:text-base">
                <span className="truncate block sm:inline">{league.tournament_name}</span>
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className="inline-flex items-center space-x-1 sm:space-x-2">
                <span className="text-blue-100 text-xs sm:text-sm">Participants:</span>
                <span className="text-white text-xs sm:text-sm md:text-base font-semibold">{participants.length}/{league.max_participants}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="w-full px-1.5 sm:px-4 lg:px-8 py-2 sm:py-4 md:py-5">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20 mb-6 sm:mb-8">
          <div className="flex space-x-1">
            {[
              { key: 'info', label: 'League Info', icon: 'ℹ️', shortLabel: 'Info' },
              { key: 'team', label: 'My Team', icon: '👥', shortLabel: 'Team' },
              { key: 'players', label: 'Players', icon: '🧢', shortLabel: 'Players' },
              { key: 'matches', label: 'Matches', icon: '🏏', shortLabel: 'Matches' },
              { key: 'standings', label: 'Standings', icon: '📊', shortLabel: 'Standings' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as any)}
                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-3 md:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-accent text-primary shadow-lg transform scale-105'
                    : 'text-white hover:text-accent hover:bg-white/10'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="w-full px-0 sm:px-4 lg:px-8">
        <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
          {activeTab === 'info' && (
            <div className="grid md:grid-cols-2 gap-8">
            {/* League Details */}
            <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-primary flex items-center">
                  <span className="mr-3">🏆</span>
                  League Details
                </h2>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 text-gray-600 hover:text-accent hover:bg-gray-100 rounded-lg transition-colors"
                  title="Toggle Settings"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">League Name:</span>
                  <div className="flex items-center space-x-2">
                    {isEditingName ? (
                      <>
                        <input
                          type="text"
                          value={editedLeagueName}
                          onChange={(e) => setEditedLeagueName(e.target.value)}
                          className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                          autoFocus
                        />
                        <button
                          onClick={async () => {
                            if (editedLeagueName.trim() && editedLeagueName.trim() !== league.name) {
                              await handleRenameLeague(editedLeagueName.trim());
                            }
                            setIsEditingName(false);
                          }}
                          disabled={isRenaming || !editedLeagueName.trim() || editedLeagueName.trim() === league.name}
                          className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Save"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingName(false);
                            setEditedLeagueName(league.name);
                          }}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          title="Cancel"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-primary font-semibold">{league.name}</span>
                        {isManager && (
                          <button
                            onClick={() => {
                              setEditedLeagueName(league.name);
                              setIsEditingName(true);
                            }}
                            className="text-accent hover:text-accent/80 transition-colors ml-2"
                            title="Edit league name"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Tournament:</span>
                  <span className="text-primary font-semibold">{league.tournament_name}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">League Type:</span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    Budget League
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    league.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : league.status === 'completed'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {league.status.charAt(0).toUpperCase() + league.status.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Visibility:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    league.is_public 
                      ? 'bg-secondary/20 text-secondary'
                      : 'bg-accent/20 text-accent'
                  }`}>
                    {league.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
                {league.join_code && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Join Code:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                        {league.join_code}
                      </span>
                      <button
                        onClick={() => handleCopyToClipboard(league.join_code!, 'Join code')}
                        className="text-accent hover:text-accent/80 transition-colors duration-200"
                        title="Copy join code"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {league.passcode && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">League Passcode:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                        {league.passcode}
                      </span>
                      <button
                        onClick={() => handleCopyToClipboard(league.passcode!, 'Passcode')}
                        className="text-accent hover:text-accent/80 transition-colors duration-200"
                        title="Copy passcode"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-primary font-medium">{new Date(league.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Participants:</span>
                  <div className="flex items-center space-x-2">
                    {isEditingMaxParticipants ? (
                      <>
                        <span className="text-primary font-semibold">{league.current_participants}/</span>
                        <input
                          type="number"
                          value={editedMaxParticipants}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= league.current_participants) {
                              setEditedMaxParticipants(val);
                            }
                          }}
                          min={league.current_participants}
                          className="w-20 px-2 py-1 bg-gray-50 border border-gray-300 rounded text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                          autoFocus
                        />
                        <button
                          onClick={async () => {
                            if (editedMaxParticipants !== league.max_participants && editedMaxParticipants >= league.current_participants) {
                              await handleUpdateMaxParticipants(editedMaxParticipants);
                            }
                            setIsEditingMaxParticipants(false);
                          }}
                          disabled={isUpdatingMaxParticipants || editedMaxParticipants === league.max_participants || editedMaxParticipants < league.current_participants}
                          className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Save"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingMaxParticipants(false);
                            setEditedMaxParticipants(league.max_participants);
                          }}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          title="Cancel"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-primary font-semibold">{league.current_participants}/{league.max_participants}</span>
                        {isManager && (
                          <button
                            onClick={() => {
                              setEditedMaxParticipants(league.max_participants);
                              setIsEditingMaxParticipants(true);
                            }}
                            className="text-accent hover:text-accent/80 transition-colors ml-2"
                            title="Edit max participants"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Teams Created:</span>
                  <span className="text-primary font-semibold">{participants.filter(p => p.total_points !== undefined).length}/{participants.length}</span>
                </div>
                {participants.find(p => p.role === 'manager' || league.created_by === p.user_id) && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">League Manager:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-primary font-medium">
                        {participants.find(p => p.role === 'manager' || league.created_by === p.user_id)?.display_name}
                      </span>
                      <span className="px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">
                        Manager
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Share Information */}
              {(league.join_code || league.passcode) && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-primary font-semibold mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Share League
                  </h4>
                  <p className="text-gray-600 text-sm mb-3">
                    Share this link with friends. They'll be able to join after logging in or signing up.
                  </p>
                  {(() => {
                    const shareUrl = `${window.location.origin}/join-league?code=${league.join_code || ''}&passcode=${league.passcode || ''}&type=budget_league`;
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            readOnly
                            value={shareUrl}
                            className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded text-gray-800 text-sm font-mono"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              showNotification('Link copied to clipboard!');
                            }}
                            className="px-4 py-2 bg-accent hover:bg-accent/90 text-primary rounded transition-colors flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy Link</span>
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {league.join_code && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              Join Code: {league.join_code}
                            </span>
                          )}
                          {league.passcode && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              Passcode: {league.passcode}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Participants */}
            <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
              <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
                <span className="mr-3">👥</span>
                Participants ({participants.length})
              </h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {participants.map((participant, index) => (
                  <div key={participant.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-primary font-medium">{participant.display_name}</p>
                        <p className="text-gray-500 text-sm">{new Date(participant.joined_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {participant.role === 'manager' && (
                      <span className="px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">
                        Manager
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Settings Section (Collapsible) */}
            {showSettings && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mt-6 col-span-2">
                <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <span className="mr-3">⚙️</span>
                  Budget League Rules & Settings
                </h2>
                
                {/* Budget League Rules */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-6">
                  <h3 className="text-xl font-bold text-primary mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Budget League Rules
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Team Formation Rules */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-primary mb-3 text-purple-600">Team Formation</h4>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-purple-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Budget Limit</div>
                            <div className="text-gray-600 text-sm">Each team has exactly 100 credits to spend on players</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-purple-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Team Size</div>
                            <div className="text-gray-600 text-sm">Must select exactly 11 players for your playing XI</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-purple-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Player Roles</div>
                            <div className="text-gray-600 text-sm">Balance your team with batsmen, bowlers, all-rounders, and wicket-keepers</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-purple-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Transfers</div>
                            <div className="text-gray-600 text-sm">Limited transfers available per tournament (set by admin)</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Scoring Rules */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-primary mb-3 text-green-600">Scoring System</h4>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-green-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Captain Selection</div>
                            <div className="text-gray-600 text-sm">Captain earns 2x points from their performance</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-green-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Vice-Captain Selection</div>
                            <div className="text-gray-600 text-sm">Vice-Captain earns 1.5x points from their performance</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-green-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Boosters</div>
                            <div className="text-gray-600 text-sm">Use boosters to multiply points for specific players or matches</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-green-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Real-time Scoring</div>
                            <div className="text-gray-600 text-sm">Points calculated based on actual player performance in matches</div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="w-3 h-3 bg-green-400 rounded-full mr-3 mt-1 flex-shrink-0"></div>
                          <div>
                            <div className="text-primary font-medium">Match Points</div>
                            <div className="text-gray-600 text-sm">Runs, wickets, catches, and other cricket actions earn points</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Settings */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-primary mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Current League Settings
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Transfer Limit:</span>
                      <span className="text-primary">Set by Admin</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">League Type:</span>
                      <span className="text-primary">Budget League</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Max Participants:</span>
                      <span className="text-primary">{league.max_participants}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">League Status:</span>
                      <span className="text-primary capitalize">{league.status}</span>
                    </div>
                  </div>
                </div>

                {/* Fantasy Points Summary */}
                {league.live_tournament_id && (
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-700 mt-2">
                    <FantasyPointsSummary tournamentId={league.live_tournament_id} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (
          <div>

            {/* DEBUG: Show loading state */}
            {userTeam && !matchNavigation && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-center text-yellow-700">
                ⏳ Loading match navigation...
              </div>
            )}
            
            {/* DEBUG: Show no matches state */}
            {userTeam && matchNavigation && (!matchNavigation.matches || matchNavigation.matches.length === 0) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg text-center text-red-700">
                ⚠️ No matches found for this tournament
              </div>
            )}

            {userTeam && matchNavigation && matchNavigation.matches && matchNavigation.matches.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  {/* Back Button */}
                  <button
                    onClick={() => handleMatchNavigation('prev')}
                    disabled={currentViewMatchIndex <= matchNavigation.team_start_match_index}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                      currentViewMatchIndex <= matchNavigation.team_start_match_index
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    &lt;&lt;
                  </button>

                  {/* Match Name */}
                  <div className="flex-1 mx-4 text-center">
                    <div className="text-lg font-bold text-gray-800 mb-1">
                      {(() => {
                        const fullName = matchNavigation.matches[currentViewMatchIndex]?.match_name || 'Match';
                        // Split by comma and take parts until we find the match number part
                        const parts = fullName.split(',').map((p: string) => p.trim());
                        // Find the part that contains "Match" (e.g., "18th Match")
                        const matchIndex = parts.findIndex((p: string) => /Match/i.test(p));
                        if (matchIndex >= 0) {
                          // Return team names + match number part
                          return parts.slice(0, matchIndex + 1).join(', ');
                        }
                        // Fallback: just return first part if no match number found
                        return parts[0] || fullName;
                      })()}
                    </div>
                    {matchNavigation.matches[currentViewMatchIndex]?.status === 'live' && (
                      <div className="text-green-700 text-sm font-medium bg-green-100 px-2 py-0.5 rounded inline-block">
                        Live
                      </div>
                    )}
                    {matchNavigation.matches[currentViewMatchIndex]?.status === 'scheduled' &&
                     matchNavigation.next_match &&
                     matchNavigation.matches[currentViewMatchIndex]?.match_id === matchNavigation.next_match?.match_id && (
                      <div className="text-yellow-600 text-sm">
                        Upcoming Match
                      </div>
                    )}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handleMatchNavigation('next')}
                    disabled={currentViewMatchIndex >= matchNavigation.matches.length - 1}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                      currentViewMatchIndex >= matchNavigation.matches.length - 1
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    &gt;&gt;
                  </button>
                </div>

                {/* Countdown Timer */}
                {matchNavigation.next_match && countdownToNext && (
                  <div className="text-center text-sm text-blue-700 mt-2 p-2 bg-blue-100 rounded-lg border border-blue-200">
                    <span className="font-semibold">Next Match:</span> {matchNavigation.next_match.match_name.split(',')[0].trim()}
                    <br />
                    <span className="text-yellow-700">⏱️ Match starts in: {countdownToNext}</span>
                    {matchNavigation.next_match.extended_by_admin && countdownToLock && (
                      <div className="text-xs text-yellow-600 mt-1">
                        ⚠️ Transfer lock extended for {matchNavigation.next_match.match_name.split(',')[0].trim()}
                        <br />
                        <span className="font-semibold">🔒 Transfers lock in: {countdownToLock}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* 15 min snapshot window: transfers locked after match start */}
                {matchNavigation?.transfers_locked_until && new Date(matchNavigation.transfers_locked_until) > new Date() && (
                  <div className="text-center text-sm text-amber-800 mt-2 p-2 bg-amber-100 rounded-lg border border-amber-300">
                    🔒 Transfers locked for 15 min after match start (saving team snapshots). Unlocks at {new Date(matchNavigation.transfers_locked_until).toLocaleTimeString()}.
                  </div>
                )}
              </div>
            )}

            {!userTeam ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Team Created</h3>
                <p className="text-gray-600 mb-6">Create your budget league team to start competing!</p>
                <button
                  onClick={handleCreateTeam}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-semibold"
                >
                  Create Team
                </button>
              </div>
            ) : (
              <div>
                <div className={`rounded-lg p-4 mb-6 border-2 ${
                  isTransferMode 
                    ? 'bg-yellow-50 border-yellow-300' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">
                          {isTransferMode && pendingTransfers.length > 0 ? 'Projected Credits' : 'Credits'}:
                        </span>
                        <span className={`font-bold ${
                          isTransferMode && transferModeTeam?.length
                            ? (getProjectedBudget() <= 100 && getProjectedBudget() >= 0)
                              ? 'text-green-600'
                              : 'text-red-600'
                            : 'text-gray-800'
                        }`}>
                          {isTransferMode && transferModeTeam?.length
                            ? `${Number(getProjectedBudget()).toFixed(1)}/100`
                            : `${(userTeam?.players?.length ? userTeam.players.reduce((s: number, p: any) => s + (Number(p.credits) || 0), 0) : Number(userTeam?.total_credits_used) || 0).toFixed(1)}/100`
                          }
                        </span>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                        <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-lg border border-blue-200">
                          <span className="text-gray-700 font-medium">Transfers:</span>
                          <span className="font-bold text-blue-700 text-lg">
                            {matchNavigation?.transfers_left !== undefined 
                              ? matchNavigation.transfers_left 
                              : (userTeam?.transfers_left !== undefined ? userTeam.transfers_left : 'N/A')}
                          </span>
                        </div>
                        {nextMatchStaging && Number(nextMatchStaging.effective_transfers) > 0 && (
                          <div className="flex items-center space-x-2 bg-purple-50 px-3 py-1 rounded-lg border border-purple-200">
                            <span className="text-gray-700 font-medium">Pending:</span>
                            <span className="font-bold text-purple-700 text-lg">{nextMatchStaging.effective_transfers}</span>
                          </div>
                        )}
                        {(() => {
                          const boosterSource = (viewMatchId && viewMatchSnapshot?.applied_booster_id != null) ? viewMatchSnapshot : nextMatchStaging;
                          const appliedId = boosterSource?.applied_booster_id;
                          const name = (boosterSource === viewMatchSnapshot && viewMatchSnapshot?.applied_booster_name) ? viewMatchSnapshot.applied_booster_name : enabledBoosters.find(b => b.id === appliedId)?.name;
                          if (!appliedId || !name) return null;
                          return (
                            <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-lg border-2 border-amber-400 shadow-sm ring-1 ring-amber-300/50">
                              <span className="text-amber-900 font-bold text-sm uppercase tracking-wide">⚡ {name} Applied</span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        {isTransferMode && (
                          <button
                            onClick={handleTransferModeToggle}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          onClick={isTransferMode ? handleTransferModeToggle : handleMakeTransfers}
                          disabled={
                            (userTeam.is_locked && !isTransferMode) ||
                            !!(matchNavigation?.transfers_locked_until && new Date(matchNavigation.transfers_locked_until) > new Date())
                          }
                          className={`px-4 py-2 rounded-lg text-sm font-medium ${
                            (userTeam.is_locked && !isTransferMode) ||
                            (matchNavigation?.transfers_locked_until && new Date(matchNavigation.transfers_locked_until) > new Date())
                              ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                              : isTransferMode
                                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {matchNavigation?.transfers_locked_until && new Date(matchNavigation.transfers_locked_until) > new Date()
                            ? 'Transfers locked (15 min)'
                            : userTeam.is_locked && !isTransferMode
                              ? 'Team Locked'
                              : isTransferMode
                                ? 'Transfers'
                                : 'Transfer'
                          }
                        </button>
                      </div>
                    </div>
                    
                    {/* Collapsible Transfer Details - in transfer mode use transferModeTeam; else nextMatchStaging */}
                    {(() => {
                      const stagedList = nextMatchStaging?.staged_players
                        ? (Array.isArray(nextMatchStaging.staged_players) ? nextMatchStaging.staged_players : JSON.parse(nextMatchStaging.staged_players || '[]'))
                        : null;
                      const baselineList = nextMatchStaging?.last_snapshot_players
                        ? (Array.isArray(nextMatchStaging.last_snapshot_players) ? nextMatchStaging.last_snapshot_players : JSON.parse(nextMatchStaging.last_snapshot_players || '[]'))
                        : null;
                      const currentPlayersForDiff = (isTransferMode && transferModeTeam)
                        ? (transferModeTeam.filter((p): p is TeamPlayer => p != null) as any[])
                        : (stagedList?.length > 0 ? stagedList : userTeam?.players) || [];
                      const baselineForDiff = baselineList?.length > 0 ? baselineList : baselinePlayers;
                      const snapshotIdsForDiff = baselineList?.length > 0
                        ? baselineList.map((p: any) => Number(p.tournament_player_id || p.id))
                        : lastSnapshotPlayerIds.map(id => Number(id));
                      if (!currentPlayersForDiff.length || snapshotIdsForDiff.length === 0) return null;
                      
                      const currentPlayerIds = currentPlayersForDiff
                        .map((p: any) => p.tournament_player_id || p.id)
                        .filter((id: any) => id != null && id !== undefined)
                        .map((id: any) => Number(id));
                      const newPlayerIds = currentPlayerIds.filter((id: number) => !snapshotIdsForDiff.includes(id));
                      const removedPlayerIds = snapshotIdsForDiff.filter((id: number) => !currentPlayerIds.includes(id));
                      const transfersApplied = Math.max(newPlayerIds.length, removedPlayerIds.length);
                      const hasBooster = !!(nextMatchStaging?.applied_booster_id);
                      const showTransferDetails = transfersApplied > 0 || hasBooster;
                      
                      if (showTransferDetails) {
                        const newPlayers = currentPlayersForDiff.filter((p: any) => {
                          const pid = Number(p.tournament_player_id || p.id);
                          return newPlayerIds.includes(pid);
                        });
                        const removedPlayers = removedPlayerIds.map((rid: number) =>
                          (Array.isArray(baselineForDiff) ? baselineForDiff : baselinePlayers).find((p: any) => Number(p.tournament_player_id || p.id) === rid)
                        ).filter(Boolean) as any[];
                        const transactions = newPlayers.slice(0, removedPlayers.length).map((inP: any, i: number) => ({
                          inPlayer: inP,
                          outPlayer: removedPlayers[i]
                        })).filter((t: any) => t.outPlayer);
                        const boosterName = hasBooster ? (enabledBoosters.find(b => b.id === nextMatchStaging?.applied_booster_id)?.name ?? 'Booster') : null;
                        
                        return (
                          <div className="w-full">
                            <div 
                              className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => setIsTransferDetailsExpanded(!isTransferDetailsExpanded)}
                            >
                              <span className="text-gray-700 font-medium text-sm">View Transfer Details</span>
                              <span className="text-gray-500 text-sm">
                                {isTransferDetailsExpanded ? '▼' : '▶'}
                              </span>
                            </div>
                            
                            {isTransferDetailsExpanded && (
                              <div className="mt-2 bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                                <div className="space-y-0">
                                  {hasBooster && boosterName && (
                                    <div className="flex items-center gap-2 bg-amber-100 px-3 py-1.5 rounded-lg border-2 border-amber-400 shadow-sm w-fit mb-1">
                                      <span className="text-amber-900 font-bold text-xs uppercase tracking-wide">⚡ {boosterName} Applied</span>
                                    </div>
                                  )}
                                  {transactions.map((t: { inPlayer: any; outPlayer: any }, idx: number) => (
                                    <div key={`${t.inPlayer.tournament_player_id ?? t.inPlayer.id}-${idx}`} className="flex items-center gap-2 py-0.5 border-b border-gray-100 last:border-0">
                                      <div className="flex-1 text-sm">
                                        <span className="text-red-600">Out: {t.outPlayer?.name || 'Unknown'}</span>
                                        <span className="mx-2 text-gray-400">→</span>
                                        <span className="text-green-700 font-medium">In: {t.inPlayer.name || 'Unknown'}</span>
                                        <span className="text-gray-500 ml-1">({t.inPlayer.credits ?? 0} cr)</span>
                                      </div>
                                      {isTransferMode && (
                                        <button type="button" onClick={() => handleRevertOneTransferInTeam(t.inPlayer, t.outPlayer)} className="text-red-600 hover:text-red-700 p-1 rounded" title="Revert this change" aria-label="Revert">✕</button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                </div>


                {/* C/VC Selection in Transfer Mode */}
                {isTransferMode && (() => {
                  // Drop & Add: use filled slots; else final team after pending transfers
                  const finalTeamPlayers = (transferModeTeam?.length
                    ? transferModeTeam.filter((p): p is TeamPlayer => p != null)
                    : (userTeam?.players ?? []).map((player: any) => {
                        const playerId = player.tournament_player_id || player.id;
                        const transfer = pendingTransfers.find(t => (t.out_player.tournament_player_id || t.out_player.id) === playerId);
                        return transfer ? { ...transfer.in_player, tournament_player_id: transfer.in_player.tournament_player_id || transfer.in_player.id } : player;
                      })
                  );

                  return (
                    <div className="mb-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Captain & Vice-Captain Selection</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white/80 text-sm font-medium mb-2">Captain *</label>
                          <select
                            value={transferModeCaptain || ''}
                            onChange={(e) => {
                              const captainId = parseInt(e.target.value);
                              setTransferModeCaptain(captainId || null);
                              if (captainId === transferModeViceCaptain) {
                                setTransferModeViceCaptain(null);
                              }
                            }}
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="" className="text-gray-900">Select Captain</option>
                            {finalTeamPlayers.map((player) => {
                              const playerId = player.tournament_player_id || player.id;
                              return (
                                <option key={playerId} value={playerId} className="text-gray-900">
                                  {player.name} ({player.credits} cr)
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="block text-white/80 text-sm font-medium mb-2">Vice-Captain *</label>
                          <select
                            value={transferModeViceCaptain || ''}
                            onChange={(e) => {
                              const vcId = parseInt(e.target.value);
                              setTransferModeViceCaptain(vcId || null);
                              if (vcId === transferModeCaptain) {
                                setTransferModeCaptain(null);
                              }
                            }}
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="" className="text-gray-900">Select Vice-Captain</option>
                            {finalTeamPlayers.map((player) => {
                              const playerId = player.tournament_player_id || player.id;
                              return (
                                <option key={playerId} value={playerId} className="text-gray-900">
                                  {player.name} ({player.credits} cr)
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>
                      {(!transferModeCaptain || !transferModeViceCaptain) && (
                        <p className="text-red-600 font-medium text-sm mt-2">* Please select both Captain and Vice-Captain before confirming transfers</p>
                      )}
                      {/* Booster: show current applied + button to open modal */}
                      <div className="mt-4 pt-3 border-t border-blue-500/30">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            {nextMatchStaging?.applied_booster_id ? (
                              <span className="text-white/90 text-sm">
                                Booster applied for next match: <strong>{enabledBoosters.find(b => b.id === nextMatchStaging.applied_booster_id)?.name ?? 'Booster'}</strong>
                                {nextMatchStaging.triple_threat_player_id && (
                                  <span className="text-white/70 text-xs ml-1">(3× player selected)</span>
                                )}
                                {nextMatchStaging.impact_player_id && (
                                  <span className="text-white/70 text-xs ml-1">(2× player selected)</span>
                                )}
                                {enabledBoosters.find(b => b.id === nextMatchStaging.applied_booster_id)?.name === 'Double Down' && (
                                  <span className="text-white/70 text-xs ml-1">(2× all)</span>
                                )}
                                {enabledBoosters.find(b => b.id === nextMatchStaging.applied_booster_id)?.name === 'Power Player' && (
                                  <span className="text-white/70 text-xs ml-1">(2× C)</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-white/70 text-sm">No booster applied for next match</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setBoosterModalSelectedId(nextMatchStaging?.applied_booster_id ?? null);
                              setBoosterModalTTPlayerId(nextMatchStaging?.triple_threat_player_id ?? null);
                              setBoosterModalIPPlayerId(nextMatchStaging?.impact_player_id ?? null);
                              setShowBoosterModal(true);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
                          >
                            {nextMatchStaging?.applied_booster_id ? 'Change booster' : 'Apply booster'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-800">Playing XI</h3>
                      <div className="flex rounded-lg border border-gray-300 bg-gray-100 p-0.5" role="tablist">
                        <button
                          type="button"
                          onClick={() => setTeamViewModePersisted('list')}
                          className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                            teamViewMode === 'list' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          List
                        </button>
                        <button
                          type="button"
                          onClick={() => setTeamViewModePersisted('field')}
                          className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                            teamViewMode === 'field' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Field
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {currentUserId != null && standings.length > 0 && (() => {
                        const myStanding = standings.find((s: Standing) => s.user_id === currentUserId);
                        const totalPointsAllMatches = myStanding?.total_points ?? 0;
                        return (
                          <div className="text-sm text-black">
                            Total Points: <span className="font-bold text-lg text-blue-500">{Number(totalPointsAllMatches).toFixed(1)} pts</span>
                          </div>
                        );
                      })()}
                      {(() => {
                        const currentMatch = matchNavigation?.matches?.[currentViewMatchIndex];
                        if (!currentMatch) return null;
                        
                        // Show points for all matches that have points calculated (completed, abandoned, etc.)
                        // Don't show for scheduled/live matches unless they have points
                        
                        // First, try to use team_total_points from snapshot (most accurate)
                        if (viewMatchSnapshot?.team_total_points !== undefined && viewMatchSnapshot.team_total_points !== null) {
                          const totalPoints = parseFloat(viewMatchSnapshot.team_total_points);
                          const isNegative = totalPoints < 0;
                          return (
                            <div className="text-sm text-black">
                              Match Points: <span className={`font-bold text-lg ${isNegative ? 'text-red-500' : 'text-orange-500'}`}>{totalPoints.toFixed(1)} pts</span>
                            </div>
                          );
                        }
                        
                        // Fallback: Calculate from individual player points
                        const pointsSource = viewMatchPoints || (recentMatchInfo && currentMatch.match_id === recentMatchInfo.id ? recentMatchPoints : null);
                        if (!pointsSource) {
                          console.log('⚠️ No points source available for match:', currentMatch.match_id, 'status:', currentMatch.status);
                          // For matches without points, show 0 (scheduled/live matches)
                          if (currentMatch.status === 'scheduled' || currentMatch.status === 'live') {
                            return (
                              <div className="text-sm text-black">
                                Match Points: <span className="font-bold text-lg text-orange-500">0.0 pts</span>
                              </div>
                            );
                          }
                          return null;
                        }
                        
                        // Use snapshot players if available (for historical matches), otherwise use current team
                        const playersToUse = viewMatchSnapshot?.players || userTeam?.players || [];
                        if (playersToUse.length === 0) {
                          console.log('⚠️ No players available for calculating total');
                          return null;
                        }
                        
                        let totalPoints = 0;
                        let hasAnyPoints = false;
                        let hasAnyPlayerWithPoints = false;
                        playersToUse.forEach((player: any) => {
                          const playerId = player.tournament_player_id || player.id;
                          const playerPoints = pointsSource[playerId];
                          if (playerPoints) {
                            const points = parseFloat(playerPoints.final_points || playerPoints.total_points || 0);
                            totalPoints += points;
                            hasAnyPoints = true;
                            if (points !== 0) {
                              hasAnyPlayerWithPoints = true;
                            }
                            console.log(`📊 Player ${playerId}: ${points} points (total so far: ${totalPoints})`);
                          }
                        });
                        
                        console.log(`📊 Final total points: ${totalPoints}, hasAnyPoints: ${hasAnyPoints}, hasAnyPlayerWithPoints: ${hasAnyPlayerWithPoints}`);
                        
                        // Show total if we have any points data (even if 0 or negative)
                        // Always show for completed/abandoned matches, even if 0
                        if (hasAnyPoints || currentMatch.status === 'completed' || currentMatch.status === 'abandoned') {
                          const isNegative = totalPoints < 0;
                          return (
                            <div className="text-sm text-black">
                              Match Points: <span className={`font-bold text-lg ${isNegative ? 'text-red-500' : totalPoints === 0 ? 'text-orange-500' : 'text-orange-500'}`}>{totalPoints.toFixed(1)} pts</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {isTransferMode && (
                        <div className="flex flex-wrap items-center gap-2">
{userTeam && (
                              <button
                              onClick={handleSaveTransfers}
                              disabled={isSavingStaging || (isTransferMode && (!transferModeCaptain || !transferModeViceCaptain)) || (() => {
                                const b = enabledBoosters.find(x => x.id === nextMatchStaging?.applied_booster_id);
                                return (b?.name === 'Triple Threat' && !nextMatchStaging?.triple_threat_player_id) || (b?.name === 'Impact Player' && !nextMatchStaging?.impact_player_id);
                              })()}
                              title="Save your team for the next match. Transfers are deducted when the match locks."
                              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                isSavingStaging 
                                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                  : 'bg-green-600 hover:bg-green-700 text-white'
                              }`}
                            >
                              {isSavingStaging ? 'Saving...' : 'Save'}
                            </button>
                          )}
                          <button
                            onClick={handleResetTeam}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                          >
                            Reset
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {/* Use snapshot players when viewing a specific match, otherwise use current team */}
                    {(() => {
                      // Show team only for: previous match(es), current match, next match. For match 27+ show placeholder.
                      const currentMatch = matchNavigation?.matches?.[currentViewMatchIndex];
                      const currentMatchStatus = currentMatch?.status;
                      const nextMatchIndex = matchNavigation?.next_match
                        ? matchNavigation.matches?.findIndex((m: any) => m.match_id === matchNavigation.next_match?.match_id) ?? -1
                        : -1;
                      // Schedule-based: "viewing next match" = carousel index matches next match from schedule (no viewMatchId)
                      const isNextMatch = nextMatchIndex >= 0 && currentViewMatchIndex === nextMatchIndex;
                      const isFutureMatch = nextMatchIndex >= 0 && currentViewMatchIndex > nextMatchIndex;

                      if (isFutureMatch) {
                        return (
                          <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-6 text-center text-slate-700">
                            <p className="font-semibold">Team for this match will be set when it becomes the next match</p>
                            <p className="text-sm mt-2">Make your transfers and select Captain/Vice-Captain when this match appears as the upcoming match.</p>
                          </div>
                        );
                      }

                      // Determine which players to display: next match → staging; has snapshot → snapshot; else current team
                      const hasStaging = isNextMatch && nextMatchStaging && nextMatchStaging.staged_players;
                      const hasSnapshot = viewMatchId && viewMatchSnapshot?.players && viewMatchSnapshot.players.length > 0;
                      const noSnapshotForHistorical = viewMatchId && viewMatchSnapshot?.no_snapshot_for_historical_match === true;
                      const isLiveOrCompleted = currentMatchStatus === 'live' || currentMatchStatus === 'completed' || currentMatchStatus === 'abandoned';
                      // Booster source for badges: use snapshot when viewing that match (so badges persist after lock), else staging
                      const boosterForBadges = (viewMatchId && viewMatchSnapshot?.applied_booster_id != null) ? viewMatchSnapshot : nextMatchStaging;
                      const boosterNameForView = (boosterForBadges === viewMatchSnapshot && viewMatchSnapshot?.applied_booster_name) ? viewMatchSnapshot.applied_booster_name : enabledBoosters.find(b => b.id === boosterForBadges?.applied_booster_id)?.name;

                      let playersToDisplay: any[] = [];
                      if (noSnapshotForHistorical) {
                        // Backend said no snapshot for this past match – do NOT show current team
                        playersToDisplay = [];
                      } else {
                        playersToDisplay = userTeam?.players || [];
                      }
                      
                      if (hasStaging && !isLiveOrCompleted && !noSnapshotForHistorical) {
                        // Use staged players ONLY if viewing next match AND match is not live/completed
                        const stagedPlayers = Array.isArray(nextMatchStaging.staged_players)
                          ? nextMatchStaging.staged_players
                          : JSON.parse(nextMatchStaging.staged_players || '[]');
                        playersToDisplay = stagedPlayers.map((p: any) => ({
                          tournament_player_id: p.tournament_player_id || p.id,
                          player_id: p.player_id,
                          name: p.name || p.player_name,
                          role: p.role,
                          team: p.team || p.team_name,
                          credits: parseFloat(p.credits || 9),
                          is_captain: p.is_captain || (nextMatchStaging.captain_id && (p.tournament_player_id || p.id) === nextMatchStaging.captain_id),
                          is_vice_captain: p.is_vice_captain || (nextMatchStaging.vice_captain_id && (p.tournament_player_id || p.id) === nextMatchStaging.vice_captain_id)
                        }));
                      } else if (hasSnapshot) {
                        // Use snapshot players for live/completed/historical matches (locked team for that match)
                        playersToDisplay = viewMatchSnapshot.players;
                      } else if (!noSnapshotForHistorical) {
                        playersToDisplay = userTeam?.players || [];
                      }
                      
                      if (noSnapshotForHistorical) {
                        return (
                          <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-amber-800">
                            <p className="font-semibold">Team snapshot not available for this match</p>
                            <p className="text-sm mt-1">{viewMatchSnapshot?.message || 'The locked team for this match was not saved. Points may still count in standings if they were calculated before transfers.'}</p>
                          </div>
                        );
                      }
                      
                      const sortedPlayers = [...playersToDisplay].sort((a: any, b: any) => {
                        const getRoleOrder = (role: string): number => {
                          switch (role) {
                            case 'Batsman': return 1;
                            case 'WK-Batsman': return 2;
                            case 'All-Rounder': return 3;
                            case 'Bowler': return 4;
                            default: return 5;
                          }
                        };
                        return getRoleOrder(a.role) - getRoleOrder(b.role);
                      });
                      
                      if (teamViewMode === 'field') {
                        // Field view: role-based rows (Wicket-keepers, Batters, All-Rounders, Bowlers) on green field – like reference.
                        const fieldPlayers: (TeamPlayer | any)[] = isTransferMode && transferModeTeam && transferModeTeam.length > 0
                          ? transferModeTeam.filter((p): p is TeamPlayer => p != null)
                          : sortedPlayers;
                        const filled = fieldPlayers;
                        const totalFilled = filled.length;
                        const roleGroups: { role: string; label: string; players: (TeamPlayer | any)[] }[] = [
                          { role: 'WK-Batsman', label: 'Wicket-keepers', players: filled.filter((p: any) => p.role === 'WK-Batsman') },
                          { role: 'Batsman', label: 'Batsmen', players: filled.filter((p: any) => p.role === 'Batsman') },
                          { role: 'All-Rounder', label: 'All-Rounders', players: filled.filter((p: any) => p.role === 'All-Rounder') },
                          { role: 'Bowler', label: 'Bowlers', players: filled.filter((p: any) => p.role === 'Bowler') }
                        ];
                        // Transfer symbol for transferred-in players (field and list view)
                        const transferIconImg = (
                          <img src={`${process.env.PUBLIC_URL || ''}/transfer-icon.png`} alt="Transferred in" className="w-8 h-8 flex-shrink-0 object-contain" title="Transferred in" />
                        );

                        // Player silhouette: image from public folder (public/PlayerIcon.png)
                        const playerIconUrl = `${process.env.PUBLIC_URL || ''}/PlayerIcon.png`;
                        const PlayerIconSilhouette = ({ className }: { className?: string }) => (
                          <img src={playerIconUrl} alt="" className={className} style={{ objectFit: 'contain' }} />
                        );

                        // Striped green canvas: bigger stripes; pitch in center
                        const stripedGreenStyle = {
                          backgroundImage: 'repeating-linear-gradient(90deg, #22c55e 0px, #22c55e 120px, #16a34a 120px, #16a34a 240px)',
                        };
                        return (
                          <div
                            className="rounded-2xl overflow-visible border-2 border-green-600 shadow-inner text-center relative min-h-[160px]"
                            style={{ ...stripedGreenStyle, overflow: 'hidden' }}
                          >
                            <div className="relative p-2 sm:p-3 md:p-4 max-w-4xl mx-auto overflow-visible">
                              {/* Pitch in the center - behind content so it doesn't block clicks */}
                              <div
                                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[20%] min-w-[64px] max-w-[104px] h-[52%] min-h-[120px] rounded-sm border-2 border-amber-200/90 pointer-events-none shadow-inner z-0"
                                style={{ background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 30%, #fcd34d 50%, #fde68a 70%, #fef3c7 100%)' }}
                                aria-hidden
                              />
                              {roleGroups.map(({ role, label, players }) => {
                                const count = players.length;
                                const maxForRole = ROLE_MAX[role] ?? 4;
                                const canAddThisRole = canAddPlayerForComposition(filled, role);
                                const showPlus = isTransferMode && totalFilled < 11 && count < maxForRole && canAddThisRole;
                                return (
                                  <div key={role} className="relative mb-1 sm:mb-1.5 last:mb-0 flex flex-col items-center z-[1]">
                                    <div className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider mb-0.5 sm:mb-1 text-center w-full" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{label}</div>
                                    <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 justify-center items-center">
                                      {players.map((player: any) => {
                                        const playerId = player.tournament_player_id || player.id;
                                        const numId = Number(playerId);
                                        const isTransferredIn = (lastSnapshotPlayerIds.length > 0 && !lastSnapshotPlayerIds.includes(playerId)) || recentlyAddedPlayerIds.has(numId);
                                        const playerPoints = getPlayerRecentPoints && getPlayerRecentPoints(playerId);
                                        const pointsText = playerPoints != null ? `${playerPoints.final_points} pts` : (currentMatch?.status === 'live' || currentMatch?.status === 'scheduled' ? '0 pts' : '–');
                                        const creditsStr = Number(player.credits) === Math.floor(Number(player.credits)) ? String(player.credits) : Number(player.credits).toFixed(1);
                                        return (
                                          <div key={playerId} className="flex flex-col items-center relative">
                                            <div className="flex flex-row items-center gap-0">
                                              <div className="relative inline-block">
                                                <PlayerIconSilhouette className="w-24 h-24 sm:w-28 sm:h-28 object-contain block" />
                                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-0.5 px-1.5 py-0.5 rounded bg-green-900/90 text-white text-xs font-bold whitespace-nowrap" style={{ textShadow: '0 0 1px rgba(0,0,0,0.8)' }}>{pointsText}</div>
                                                {isTransferMode && (
                                                  <button type="button" className="absolute -top-0.5 -right-0.5 z-20 w-7 h-7 sm:w-8 sm:h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-base font-bold shadow-md border-2 border-white" title="Remove" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDropPlayer(player); }}>−</button>
                                                )}
                                              </div>
                                            </div>
                                            <div className="-mt-4 sm:-mt-5 text-center">
                                              <div className="font-bold text-sm text-green-900 truncate max-w-[100px] flex items-center justify-center gap-1 mx-auto flex-wrap">
                                                {getPlayerDisplayName(player.name)}
                                                {isPlayerForeign(player) && <ForeignPlayerIcon />}
                                                {player.is_captain && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-700 text-xs font-bold rounded border border-yellow-500/30">C</span>}
                                                {player.is_vice_captain && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-700 text-xs font-bold rounded border border-blue-500/30">VC</span>}
                                                {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.triple_threat_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">3×</span>}
                                                {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.impact_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                                                {boosterNameForView === 'Double Down' && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                                                {boosterNameForView === 'Power Player' && player.is_captain && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                                                {isTransferredIn && <span className="inline-flex flex-shrink-0">{transferIconImg}</span>}
                                              </div>
                                              <div className="text-green-600 text-xs font-medium">{creditsStr} Cr</div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      {showPlus && (
                                        <div className="flex flex-col items-center">
                                          <button type="button" onClick={() => handleOpenAdd(role)} className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center text-2xl font-bold bg-green-600 hover:bg-green-500 text-white border-2 border-dashed border-green-400 shadow-md" title="Add player">+</button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      
                      // Drop & Add list view: E empty circular + at TOP, then filled players (sorted by role)
                      if (isTransferMode && transferModeTeam?.length === 11) {
                        const filled = transferModeTeam.filter((p): p is TeamPlayer => p != null).sort((a, b) => getRoleOrderForSort(a.role) - getRoleOrderForSort(b.role));
                        const emptyCount = 11 - filled.length;
                        return (
                          <>
                            {Array.from({ length: emptyCount }, (_, i) => (
                              <div key={`empty-${i}`} className="rounded-lg p-3 flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300">
                                <button type="button" onClick={() => handleOpenAdd(null)} className="bg-green-600 hover:bg-green-700 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold" title="Add player">+</button>
                              </div>
                            ))}
                            {filled.map((player) => {
                              const playerId = player.tournament_player_id || player.id;
                              const numId = Number(playerId);
                              const isTransferredInList = (lastSnapshotPlayerIds.length > 0 && !lastSnapshotPlayerIds.includes(playerId)) || recentlyAddedPlayerIds.has(numId);
                              return (
                                <div key={playerId} className="rounded-lg p-3 flex items-center justify-between bg-white/90 border border-gray-300">
                                  <div className="flex-1 flex items-center space-x-2">
                                    <span className={player.role === 'Bowler' ? 'text-sm' : 'text-lg'}>{getRoleIcon(player.role)}</span>
                                    <div className="font-semibold text-gray-800 flex items-center gap-1">{player.name}{isPlayerForeign(player) && <ForeignPlayerIcon />}
                                    {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.triple_threat_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded">3×</span>}
                                    {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.impact_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded">2×</span>}
                                    {boosterNameForView === 'Double Down' && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded">2×</span>}
                                    {boosterNameForView === 'Power Player' && player.is_captain && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded">2×</span>}
                                    </div>
                                    {isTransferredInList && (
                                      <span className="inline-flex flex-shrink-0">
                                        <img src={`${process.env.PUBLIC_URL || ''}/transfer-icon.png`} alt="Transferred in" className="w-8 h-8 object-contain" title="Transferred in" />
                                      </span>
                                    )}
                                    <span className="text-sm text-gray-600">({getTeamShortName(player.team)})</span>
                                    <div className="text-sm text-gray-600">{Number(player.credits) === Math.floor(player.credits) ? player.credits : Number(player.credits).toFixed(1)} cr</div>
                                    {player.is_captain && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-600 text-xs font-bold rounded">C</span>}
                                    {player.is_vice_captain && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-600 text-xs font-bold rounded">VC</span>}
                                  </div>
                                  <button type="button" onClick={() => handleDropPlayer(player)} className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold" title="Remove">−</button>
                                </div>
                              );
                            })}
                          </>
                        );
                      }
                      
                      return sortedPlayers.map((player: any, index: number) => {
                        const playerId = player.tournament_player_id || player.id;
                        const numId = Number(playerId);
                        const isNewPlayer = lastSnapshotPlayerIds.length > 0 && !lastSnapshotPlayerIds.includes(playerId);
                        const isChangedPlayer = changedPlayers.has(playerId);
                        const isRecentlyAdded = recentlyAddedPlayerIds.has(numId);
                        const originalPlayer = stagingState?.last_snapshot_players?.find((p: any) => p.tournament_player_id === playerId);
                        
                        const isTransferredIn = isNewPlayer || isRecentlyAdded;
                        return (
                        <div key={player.tournament_player_id || player.id || index} className={`rounded-lg p-3 flex items-center justify-between ${
                          isTransferMode 
                            ? 'bg-white/90 border border-gray-300 hover:bg-white transition-colors' 
                            : 'bg-white/80 border border-gray-200'
                        }`}>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {player.role === 'Batsman' ? (
                                <span className="flex items-center">
                                  {getRoleIcon(player.role)}
                                </span>
                              ) : (
                                <span className={`${
                                  player.role === 'Bowler' ? 'text-sm' : 
                                  player.role === 'WK-Batsman' ? 'text-lg filter sepia-100 saturate-200 hue-rotate-[20deg] brightness-90' : 
                                  'text-lg'
                                }`}>
                                  {getRoleIcon(player.role)}
                                </span>
                              )}
                              <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                                {player.name}
                                {isPlayerForeign(player) && <ForeignPlayerIcon />}
                                {isTransferredIn && (
                                  <span className="inline-flex flex-shrink-0">
                                    <img src={`${process.env.PUBLIC_URL || ''}/transfer-icon.png`} alt="Transferred in" className="w-8 h-8 object-contain" title="Transferred in" />
                                  </span>
                                )}
                                {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.triple_threat_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">3×</span>}
                                {Number(player.tournament_player_id ?? player.id) === Number(boosterForBadges?.impact_player_id) && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                                {boosterNameForView === 'Double Down' && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                                {boosterNameForView === 'Power Player' && player.is_captain && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-800 text-xs font-bold rounded border border-amber-500/30">2×</span>}
                              </div>
                              <div className="text-sm text-gray-600">{player.credits} cr</div>
                              {player.is_captain && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-600 text-xs font-bold rounded border border-yellow-500/30">
                                  C
                                </span>
                              )}
                              {player.is_vice_captain && (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-600 text-xs font-bold rounded border border-blue-500/30">
                                  VC
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">{player.team}</div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              {(() => {
                                const currentMatch = matchNavigation?.matches?.[currentViewMatchIndex];
                                const playerPoints = getPlayerRecentPoints(player.tournament_player_id || player.id);
                                
                                if (playerPoints) {
                                  // Show actual points for completed matches
                                  return (
                                    <div>
                                      <div className="font-bold text-xl text-green-600">{playerPoints.final_points} pts</div>
                                      {(playerPoints.is_captain || playerPoints.is_vice_captain) && (
                                        <div className="text-gray-500 text-xs mt-0.5">
                                          ({playerPoints.base_points} × {playerPoints.is_captain ? '2' : '1.5'})
                                        </div>
                                      )}
                                    </div>
                                  );
                                } else if (currentMatch && (currentMatch.status === 'live' || currentMatch.status === 'scheduled')) {
                                  // Show 0 pts for live/scheduled matches
                                  return (
                                    <div className="font-bold text-xl text-gray-400">0 pts</div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            {isTransferMode && (
                              <div className="flex items-center space-x-2">
                                {isChangedPlayer && originalPlayer && (
                                  <button
                                    onClick={() => handleUndoPlayerChange(playerId)}
                                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded text-sm font-medium"
                                    title="Undo this change"
                                  >
                                    ↶ Undo
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handlePlayerReplace(player)}
                                  className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-4 py-2.5 rounded text-sm font-medium min-h-[44px] min-w-[44px] touch-manipulation select-none"
                                >
                                  Replace
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}



        {activeTab === 'standings' && (
          <div className="space-y-8">
            {/* Standings Section */}
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
                <span className="mr-3">🏆</span>
                League Standings
              </h2>
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full text-left min-w-[280px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 font-semibold text-sm sm:text-base">Rank</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 font-semibold text-sm sm:text-base">Player</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 font-semibold text-center text-sm sm:text-base">Total Points</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 font-semibold text-center text-sm sm:text-base whitespace-nowrap">Boosters Used</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 font-semibold w-16 sm:w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-gray-500">
                            No standings data available yet.
                          </td>
                        </tr>
                      ) : (
                        standings.map((standing, index) => {
                          const totalPoints = standing.total_points !== undefined && standing.total_points !== null 
                            ? Number(standing.total_points) 
                            : 0;
                          const teamId = standing.team_id;
                          const mostRecentMatchId = getMostRecentMatchId(matches);
                          const canViewTeam = teamId != null && mostRecentMatchId != null;
                          return (
                            <tr
                              key={standing.user_id || index}
                              className={`border-b border-gray-100 transition group ${canViewTeam ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                              onClick={() => {
                                if (!canViewTeam) return;
                                if (!teamId) {
                                  showNotification('Team data is not available.');
                                  return;
                                }
                                if (!mostRecentMatchId) {
                                  showNotification('No match snapshot available yet.');
                                  return;
                                }
                                setSelectedSnapshot({ teamId, matchId: mostRecentMatchId });
                                loadSnapshotModal(teamId, mostRecentMatchId);
                              }}
                            >
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-800">
                                <div className="flex items-center">
                                  <span className="font-bold mr-2 text-sm sm:text-base">#{standing.rank || index + 1}</span>
                                  {index < 3 && (
                                    <span className="text-lg">
                                      {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-800 font-semibold text-sm sm:text-base truncate max-w-[120px] sm:max-w-none">{standing.display_name || 'Unknown'}</td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-primary text-center font-bold text-base sm:text-lg">
                                {totalPoints.toFixed(1)}
                              </td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-gray-700 font-medium text-sm sm:text-base whitespace-nowrap">
                                {(standing.boosters_used ?? 0)}/7
                              </td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-right">
                                {canViewTeam && (
                                  <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition font-medium inline-flex items-center gap-1">
                                    View team
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* League Statistics Section - Removed for now */}
          </div>
        )}

        {activeTab === 'players' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-primary">Players</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadTeamBuilderPlayers()}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={playersSearchQuery}
                  onChange={(e) => setPlayersSearchQuery(e.target.value)}
                  placeholder="Search by name or team"
                  className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Role</label>
                <select
                  value={playersRoleFilter}
                  onChange={(e) => setPlayersRoleFilter(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All roles</option>
                  <option value="Batsman">Batsman</option>
                  <option value="Bowler">Bowler</option>
                  <option value="All-Rounder">All-Rounder</option>
                  <option value="WK-Batsman">WK-Batsman</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Team</label>
                <select
                  value={playersTeamFilter}
                  onChange={(e) => setPlayersTeamFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All teams</option>
                  {getUniqueTeams().map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {teamBuilderPlayers.length === 0 ? (
                <div className="text-center py-12 text-gray-600">
                  No players loaded yet. Click Refresh to load players.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-gray-700 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            if (playersSortBy === 'name') {
                              setPlayersSortOrder(playersSortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setPlayersSortBy('name');
                              setPlayersSortOrder('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            Name
                            {playersSortBy === 'name' && (
                              <span>{playersSortOrder === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-gray-700 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            if (playersSortBy === 'team') {
                              setPlayersSortOrder(playersSortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setPlayersSortBy('team');
                              setPlayersSortOrder('asc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            Team
                            {playersSortBy === 'team' && (
                              <span>{playersSortOrder === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-gray-700 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            if (playersSortBy === 'credits') {
                              setPlayersSortOrder(playersSortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setPlayersSortBy('credits');
                              setPlayersSortOrder('desc');
                            }
                          }}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Credits
                            {playersSortBy === 'credits' && (
                              <span>{playersSortOrder === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-center text-gray-700 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            if (playersSortBy === 'points') {
                              setPlayersSortOrder(playersSortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setPlayersSortBy('points');
                              setPlayersSortOrder('desc');
                            }
                          }}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Points
                            {playersSortBy === 'points' && (
                              <span>{playersSortOrder === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = teamBuilderPlayers.filter(player => {
                          const matchesSearch = playersSearchQuery.trim().length === 0
                            || player.name?.toLowerCase().includes(playersSearchQuery.toLowerCase())
                            || player.player_name?.toLowerCase().includes(playersSearchQuery.toLowerCase())
                            || player.team?.toLowerCase().includes(playersSearchQuery.toLowerCase());
                          const matchesRole = playersRoleFilter === 'all' || player.role === playersRoleFilter;
                          const matchesTeam = playersTeamFilter === 'all' || player.team === playersTeamFilter;
                          return matchesSearch && matchesRole && matchesTeam;
                        });

                        const sorted = [...filtered].sort((a, b) => {
                          let compareValue = 0;
                          
                          if (playersSortBy === 'name') {
                            const nameA = (a.name || a.player_name || '').toLowerCase();
                            const nameB = (b.name || b.player_name || '').toLowerCase();
                            compareValue = nameA.localeCompare(nameB);
                          } else if (playersSortBy === 'team') {
                            const teamA = (a.team || '').toLowerCase();
                            const teamB = (b.team || '').toLowerCase();
                            compareValue = teamA.localeCompare(teamB);
                          } else if (playersSortBy === 'role') {
                            const roleA = (a.role || '').toLowerCase();
                            const roleB = (b.role || '').toLowerCase();
                            compareValue = roleA.localeCompare(roleB);
                          } else if (playersSortBy === 'credits') {
                            compareValue = Number(a.credits || 9) - Number(b.credits || 9);
                            // Secondary sort by name if credits are equal
                            if (compareValue === 0) {
                              const nameA = (a.name || a.player_name || '').toLowerCase();
                              const nameB = (b.name || b.player_name || '').toLowerCase();
                              compareValue = nameA.localeCompare(nameB);
                            }
                          } else if (playersSortBy === 'points') {
                            compareValue = Number(a.points || 0) - Number(b.points || 0);
                            // Secondary sort by name if points are equal
                            if (compareValue === 0) {
                              const nameA = (a.name || a.player_name || '').toLowerCase();
                              const nameB = (b.name || b.player_name || '').toLowerCase();
                              compareValue = nameA.localeCompare(nameB);
                            }
                          }
                          
                          return playersSortOrder === 'asc' ? compareValue : -compareValue;
                        });

                        return sorted.map((player, index) => (
                          <tr 
                            key={player.id || player.tournament_player_id} 
                            className={`border-t border-gray-200 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {player.role === 'Batsman' ? (
                                  <span className="flex items-center">
                                    {getRoleIcon(player.role)}
                                  </span>
                                ) : (
                                  <span className={player.role === 'Bowler' ? 'text-base' : 'text-xl'}>
                                    {getRoleIcon(player.role)}
                                  </span>
                                )}
                                <div className="text-gray-800 font-medium flex items-center">{player.name || player.player_name}{isPlayerForeign(player) && <ForeignPlayerIcon />}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{player.team}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-gray-800 font-semibold">{player.credits || 9}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-yellow-600 font-bold">{player.points ?? 0}</span>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-primary">Matches</h2>

            {!userTeam ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏏</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Team Available</h3>
                <p className="text-gray-600 mb-6">Create a team to view match standings and points.</p>
                <button
                  onClick={handleCreateTeam}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-semibold"
                >
                  Create Team
                </button>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Select Match</label>
                  <select
                    value={selectedMatchId || ''}
                    onChange={(e) => setSelectedMatchId(e.target.value || null)}
                    className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a match</option>
                    {matches.map((m) => {
                      const matchStatus = getMatchStatusLocal(m);
                      const formattedName = formatMatchName(m.match_name || m.name || `${m.team1_name || m.team1 || 'Team 1'} vs ${m.team2_name || m.team2 || 'Team 2'}`);
                      const formattedDate = m.match_date ? formatMatchDate(m.match_date) : '';
                      
                      return (
                        <option key={m.id} value={m.id}>
                          {formattedName} {formattedDate ? `- ${formattedDate}` : ''}
                          {matchStatus === 'Completed' ? ' (Completed)' : 
                           matchStatus === 'Abandoned' ? ' (Abandoned)' :
                           matchStatus === 'Started' ? ' (Started)' : 
                           matchStatus === 'Live' ? ' (Live)' : ' (Upcoming)'}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {selectedMatchId ? (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    {/* Standings first */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Standings for selected match</h3>
                      {matchStandings.length === 0 ? (
                        <div className="text-gray-600">No data yet for this match.</div>
                      ) : (
                        <div className="space-y-2">
                          {matchStandings.map((row) => {
                            const selectedMatch = matches.find(m => String(m.id) === String(selectedMatchId));
                            const matchStarted = selectedMatch ? getMatchStatusLocal(selectedMatch) !== 'Upcoming' : false;
                            return (
                              <div
                                key={row.team_id}
                                className="bg-white rounded-lg p-3 flex items-center justify-between border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition cursor-pointer group"
                                onClick={() => {
                                  if (!matchStarted) {
                                    showNotification('Team is visible only after the match has started.');
                                    return;
                                  }
                                  setSelectedSnapshot({ teamId: row.team_id, matchId: selectedMatchId });
                                  loadSnapshotModal(row.team_id, selectedMatchId);
                                }}
                              >
                                <div>
                                  <div className="text-gray-800 font-semibold">{row.display_name || row.team_name}</div>
                                  <div className="text-xs text-gray-600">{row.team_name}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <div className="text-lg font-bold text-primary">{row.total_points || 0} pts</div>
                                    <div className="text-xs text-gray-600">C: {row.captain_points || 0} | VC: {row.vice_captain_points || 0}</div>
                                  </div>
                                  <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition font-medium flex items-center gap-1" aria-hidden>
                                    View team
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Match Fantasy Points second */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Match Fantasy Points</h3>
                      {loadingPlayerPoints ? (
                        <div className="text-gray-600">Loading player points...</div>
                      ) : matchPlayerPoints.length === 0 ? (
                        <div className="text-gray-600">No player points available for this match.</div>
                      ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                          {matchPlayerPoints.map((p, idx) => (
                            <div key={`${p.player_id}-${idx}`} className="bg-white rounded-lg p-3 flex items-center justify-between border border-gray-200">
                              <div>
                                <div className="text-gray-800 font-semibold">{p.name || 'Player'}</div>
                                <div className="text-xs text-gray-600">
                                  {p.team || 'Team'} {p.role ? `• ${p.role}` : ''}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-primary">{p.points || 0} pts</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">Select a match to view standings.</div>
                )}
              </>
            )}
          </div>
        )}

        </div>
      </div>

      {/* Booster Modal (Transfer Mode) */}
      {showBoosterModal && userTeam && league && (() => {
        const nextMatchId = matchNavigation?.next_match?.match_id ?? matchNavigation?.next_match?.id;
        const targetMatchId = nextMatchId != null ? String(nextMatchId) : null;
        const finalTeamPlayers = (transferModeTeam?.length
          ? transferModeTeam.filter((p): p is TeamPlayer => p != null)
          : (userTeam?.players ?? []).map((player: any) => {
              const playerId = player.tournament_player_id || player.id;
              const transfer = pendingTransfers.find(t => (t.out_player.tournament_player_id || t.out_player.id) === playerId);
              return transfer ? { ...transfer.in_player, tournament_player_id: transfer.in_player.tournament_player_id || transfer.in_player.id } : player;
            })
        ).filter((p: any) => p != null);
        const captainId = transferModeCaptain ?? finalTeamPlayers.find((p: any) => p.is_captain)?.tournament_player_id ?? null;
        const viceCaptainId = transferModeViceCaptain ?? finalTeamPlayers.find((p: any) => p.is_vice_captain)?.tournament_player_id ?? null;
        const selectedBooster = enabledBoosters.find(b => b.id === boosterModalSelectedId);
        const isTripleThreat = selectedBooster?.name === 'Triple Threat';
        const isImpactPlayer = selectedBooster?.name === 'Impact Player';
        const playersExcludingCVc = finalTeamPlayers.filter((p: any) => {
          const id = p.tournament_player_id ?? p.id;
          return Number(id) !== Number(captainId) && Number(id) !== Number(viceCaptainId);
        });

        const handleApplyBooster = async () => {
          if (!targetMatchId || finalTeamPlayers.length !== 11) return;
          if (boosterModalSelectedId == null) {
            setNotification('Select a booster');
            setTimeout(() => setNotification(null), 2000);
            return;
          }
          if (isTripleThreat && !boosterModalTTPlayerId) {
            setNotification('Select a player for Triple Threat (3×)');
            setTimeout(() => setNotification(null), 2000);
            return;
          }
          if (isImpactPlayer && !boosterModalIPPlayerId) {
            setNotification('Select a player for Impact Player (2×)');
            setTimeout(() => setNotification(null), 2000);
            return;
          }
          setIsSavingBooster(true);
          try {
            const stagedPlayers = finalTeamPlayers.map((p: any) => {
              const pid = p.tournament_player_id ?? p.id;
              return {
                ...p,
                is_captain: Number(pid) === Number(captainId),
                is_vice_captain: Number(pid) === Number(viceCaptainId)
              };
            });
            const boosterOptions = {
              applied_booster_id: boosterModalSelectedId,
              triple_threat_player_id: isTripleThreat ? boosterModalTTPlayerId : null,
              impact_player_id: isImpactPlayer ? boosterModalIPPlayerId : null
            };
            await apiService.updateTeamStagingState(userTeam.id, targetMatchId, stagedPlayers, captainId, viceCaptainId, boosterOptions);
            setNextMatchStaging((prev: any) => ({
              ...prev,
              staged_players: prev?.staged_players ?? stagedPlayers,
              last_snapshot_players: prev?.last_snapshot_players,
              effective_transfers: prev?.effective_transfers ?? 0,
              captain_id: captainId,
              vice_captain_id: viceCaptainId,
              applied_booster_id: boosterModalSelectedId,
              triple_threat_player_id: boosterOptions.triple_threat_player_id,
              impact_player_id: boosterOptions.impact_player_id
            }));
            setShowBoosterModal(false);
            setNotification('✅ Booster applied for next match');
            setTimeout(() => setNotification(null), 2000);
          } catch (err: any) {
            setNotification(err?.message || 'Failed to apply booster');
            setTimeout(() => setNotification(null), 4000);
          } finally {
            setIsSavingBooster(false);
          }
        };

        const handleRemoveBooster = async () => {
          if (!targetMatchId || finalTeamPlayers.length !== 11) return;
          setIsSavingBooster(true);
          try {
            const stagedPlayers = finalTeamPlayers.map((p: any) => {
              const pid = p.tournament_player_id ?? p.id;
              return {
                ...p,
                is_captain: Number(pid) === Number(captainId),
                is_vice_captain: Number(pid) === Number(viceCaptainId)
              };
            });
            await apiService.updateTeamStagingState(userTeam.id, targetMatchId, stagedPlayers, captainId, viceCaptainId, {
              applied_booster_id: null,
              triple_threat_player_id: null,
              impact_player_id: null
            });
            setNextMatchStaging((prev: any) => ({
              ...prev,
              applied_booster_id: null,
              triple_threat_player_id: null,
              impact_player_id: null
            }));
            setBoosterModalSelectedId(null);
            setBoosterModalTTPlayerId(null);
            setBoosterModalIPPlayerId(null);
            setShowBoosterModal(false);
            setNotification('✅ Booster removed');
            setTimeout(() => setNotification(null), 2000);
          } catch (err: any) {
            setNotification(err?.message || 'Failed to remove booster');
            setTimeout(() => setNotification(null), 4000);
          } finally {
            setIsSavingBooster(false);
          }
        };

        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowBoosterModal(false)}>
            <div className="bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-600 p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-white mb-4">Apply booster for next match</h3>
              <p className="text-gray-400 text-sm mb-4">Only one booster per match. Single use per season.</p>
              <div className="space-y-2 mb-4 min-h-0">
                {enabledBoosters.map(b => {
                  const used = usedBoosterIds.includes(b.id);
                  return (
                    <label
                      key={b.id}
                      className={`flex flex-wrap items-start gap-2 gap-y-1 p-2 rounded-lg ${used ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-700/50 cursor-pointer'}`}
                    >
                      <input
                        type="radio"
                        name="booster"
                        checked={boosterModalSelectedId === b.id}
                        disabled={used}
                        onChange={() => {
                          if (used) return;
                          setBoosterModalSelectedId(b.id);
                          if (b.name !== 'Triple Threat') setBoosterModalTTPlayerId(null);
                          if (b.name !== 'Impact Player') setBoosterModalIPPlayerId(null);
                        }}
                        className="rounded-full border-gray-500 text-amber-500 mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`font-medium ${used ? 'text-gray-500' : 'text-white'}`}>{b.name}</span>
                          {used && <span className="text-amber-500/80 text-xs">Already used this season</span>}
                        </div>
                        {!used && b.description && (
                          <p className="text-gray-500 text-xs mt-0.5 break-words">{b.description}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              {enabledBoosters.length === 0 && <p className="text-gray-500 text-sm mb-4">No boosters enabled for this tournament.</p>}
              {isTripleThreat && (
                <div className="mb-4">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Select player for 3× (cannot be C/VC)</label>
                  <select
                    value={boosterModalTTPlayerId ?? ''}
                    onChange={e => setBoosterModalTTPlayerId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Select player</option>
                    {playersExcludingCVc.map((p: any) => (
                      <option key={p.tournament_player_id ?? p.id} value={p.tournament_player_id ?? p.id} className="text-gray-900">{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {isImpactPlayer && (
                <div className="mb-4">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Select player for 2× (cannot be C/VC)</label>
                  <select
                    value={boosterModalIPPlayerId ?? ''}
                    onChange={e => setBoosterModalIPPlayerId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Select player</option>
                    {playersExcludingCVc.map((p: any) => (
                      <option key={p.tournament_player_id ?? p.id} value={p.tournament_player_id ?? p.id} className="text-gray-900">{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-wrap gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowBoosterModal(false)} className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white">Cancel</button>
                {nextMatchStaging?.applied_booster_id && (
                  <button type="button" onClick={handleRemoveBooster} disabled={isSavingBooster} className="px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-600 text-white disabled:opacity-50">Remove booster</button>
                )}
                <button type="button" onClick={handleApplyBooster} disabled={isSavingBooster || !boosterModalSelectedId} className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">{isSavingBooster ? 'Saving...' : 'Apply'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Snapshot Modal */}
      {selectedSnapshot && (() => {
        const snapshotMatch = matches.find(m => String(m.id) === String(selectedSnapshot!.matchId));
        const currentMatchIndex = snapshotMatch ? matches.indexOf(snapshotMatch) : -1;
        const canGoBack = currentMatchIndex > 0;
        const liveIndex = matches.findIndex(m => getMatchStatusLocal(m) === 'Live');
        let lastCompletedIndex = -1;
        for (let i = matches.length - 1; i >= 0; i--) {
          const s = getMatchStatusLocal(matches[i]);
          if (s === 'Completed' || s === 'Abandoned') { lastCompletedIndex = i; break; }
        }
        const maxForwardIndex = liveIndex >= 0 ? liveIndex : lastCompletedIndex;
        const canGoForward = currentMatchIndex >= 0 && currentMatchIndex < matches.length - 1 && (currentMatchIndex + 1) <= maxForwardIndex;
        const isSnapshotMatchUpcoming = snapshotMatch ? getMatchStatusLocal(snapshotMatch) === 'Upcoming' : false;

        const handleSnapshotBack = () => {
          if (!canGoBack || !selectedSnapshot) return;
          const prevMatch = matches[currentMatchIndex - 1];
          const nextMatchId = String(prevMatch.id);
          setSelectedSnapshot({ teamId: selectedSnapshot.teamId, matchId: nextMatchId });
          loadSnapshotModal(selectedSnapshot.teamId, nextMatchId);
        };
        const handleSnapshotForward = () => {
          if (!canGoForward || !selectedSnapshot) return;
          const nextMatch = matches[currentMatchIndex + 1];
          const nextMatchId = String(nextMatch.id);
          setSelectedSnapshot({ teamId: selectedSnapshot.teamId, matchId: nextMatchId });
          loadSnapshotModal(selectedSnapshot.teamId, nextMatchId);
        };

        return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4 py-6" onClick={() => { setSelectedSnapshot(null); setSnapshotModalData(null); }}>
          <div
            className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); handleSnapshotBack(); }}
                  disabled={!canGoBack}
                  className="p-2 rounded-full hover:bg-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  aria-label="Previous match"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSnapshotForward(); }}
                  disabled={!canGoForward}
                  className="p-2 rounded-full hover:bg-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  aria-label="Next match"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide text-gray-500">Match Snapshot</p>
                <h3 className="text-lg sm:text-xl font-semibold text-primary truncate">
                  {snapshotMatch?.match_name || 'Match'}
                </h3>
              </div>
              <button
                onClick={() => { setSelectedSnapshot(null); setSnapshotModalData(null); }}
                className="p-2 rounded-full hover:bg-gray-200 text-gray-600 shrink-0"
                aria-label="Close snapshot"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              {isSnapshotMatchUpcoming ? (
                <div className="text-center py-12 text-gray-600">This team is hidden until the match has started.</div>
              ) : loadingSnapshot ? (
                <div className="text-center py-12 text-gray-600">Loading snapshot...</div>
              ) : snapshotModalData ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Team</div>
                      <div className="text-xl font-semibold text-gray-900">{snapshotModalData.team_name || 'Team'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Match Points</div>
                      <div className="text-2xl font-bold text-green-700">{snapshotModalData.team_total_points || 0} pts</div>
                    </div>
                  </div>
                  {(snapshotModalData.applied_booster_id != null || snapshotModalData.applied_booster_name) && (
                    <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-lg border-2 border-amber-400 shadow-sm ring-1 ring-amber-300/50 w-fit">
                      <span className="text-amber-900 font-bold text-sm uppercase tracking-wide">⚡ {snapshotModalData.applied_booster_name || enabledBoosters.find(b => b.id === snapshotModalData.applied_booster_id)?.name || 'Booster'} Applied</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    {snapshotModalData.players?.map((p: any, idx: number) => {
                      const foreignIds = Array.isArray(snapshotModalData.foreign_player_ids) ? snapshotModalData.foreign_player_ids : [];
                      const isForeign = foreignIds.includes(String(p?.player_id ?? ''));
                      const boosterName = snapshotModalData.applied_booster_name || '';
                      const tid = Number(p.tournament_player_id ?? p.id);
                      const ttId = snapshotModalData.triple_threat_player_id != null ? Number(snapshotModalData.triple_threat_player_id) : null;
                      const ipId = snapshotModalData.impact_player_id != null ? Number(snapshotModalData.impact_player_id) : null;
                      const isTripleThreat = boosterName === 'Triple Threat' && ttId != null && tid === ttId;
                      const isImpactPlayer = boosterName === 'Impact Player' && ipId != null && tid === ipId;
                      const isDoubleDown = boosterName === 'Double Down';
                      const isPowerPlayerCaptain = boosterName === 'Power Player' && p.is_captain;
                      const isForeignFury = boosterName === 'Foreign Fury' && isForeign;
                      const isDomesticDominance = boosterName === 'Domestic Dominance' && !isForeign;
                      const show2x = isImpactPlayer || isDoubleDown || isPowerPlayerCaptain || isForeignFury || isDomesticDominance;
                      return (
                      <div key={p.tournament_player_id ?? p.id ?? `p-${idx}`} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                            {p.name}
                            {isForeign && <ForeignPlayerIcon />}
                            {p.is_captain && <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">C</span>}
                            {p.is_vice_captain && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">VC</span>}
                            {isTripleThreat && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-800 font-bold rounded border border-amber-500/30">3×</span>}
                            {show2x && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-800 font-bold rounded border border-amber-500/30">2×</span>}
                          </div>
                          <div className="text-xs text-gray-600">{p.role} • {p.team}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Points</div>
                          <div className="text-lg font-bold text-gray-900">{p.total_points ?? 0} pts</div>
                          <div className="text-xs text-gray-500">Base: {p.base_points ?? 0}</div>
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-600">No snapshot found.</div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Team Builder Modal - only when creating team, not when player picker is open */}
      {showTeamBuilder && !showPlayerSelection && ReactDOM.createPortal(
        (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 min-h-[100dvh] min-h-screen overflow-hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overscrollBehavior: 'contain' }}
        >
          <div className="relative rounded-xl border border-slate-600 bg-slate-800 shadow-2xl max-w-4xl w-full flex flex-col overscroll-contain max-h-[90vh] overflow-hidden">
            {/* Team Creation View */}
            {(
              <div className="p-3 sm:p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Create Your Team</h2>
                  <button
                    onClick={() => {
                      setShowTeamBuilder(false);
                      setTeamBuilderName('');
                      setTeamSlots({
                        'wk-1': { type: 'wk', player: null },
                        'batsman-1': { type: 'batsman', player: null },
                        'batsman-2': { type: 'batsman', player: null },
                        'batsman-3': { type: 'batsman', player: null },
                        'all-rounder-1': { type: 'all-rounder', player: null },
                        'flex-1': { type: 'flex', player: null },
                        'flex-2': { type: 'flex', player: null },
                        'flex-3': { type: 'flex', player: null },
                        'bowler-1': { type: 'bowler', player: null },
                        'bowler-2': { type: 'bowler', player: null },
                        'bowler-3': { type: 'bowler', player: null }
                      });
                    }}
                    className="text-white/60 hover:text-white text-2xl"
                  >
                    ✕
                  </button>
                </div>

                {/* Team Name Input */}
                <div className="mb-6">
                  <label className="block text-white font-medium mb-2">Team Name</label>
                  <input
                    type="text"
                    value={teamBuilderName}
                    onChange={(e) => setTeamBuilderName(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:border-blue-500"
                    placeholder="Enter your team name..."
                  />
                </div>

                {/* Team Summary */}
                <div className="mb-6 bg-white/5 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-white">{getAllSelectedPlayers().length}/11</div>
                      <div className="text-sm text-white/60">Players</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">
                        {getTotalCreditsUsed().toFixed(1)}/100
                      </div>
                      <div className="text-sm text-white/60">Credits</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-yellow-400">
                        {getAllSelectedPlayers().filter(p => p.is_captain).length}/1
                      </div>
                      <div className="text-sm text-white/60">Captain</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-blue-400">
                        {getAllSelectedPlayers().filter(p => p.is_vice_captain).length}/1
                      </div>
                      <div className="text-sm text-white/60">Vice-Captain</div>
                    </div>
                  </div>
                </div>

                {/* H2H-style team build list (no slot cards) */}
                {isLoadingTeamBuilder ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-white/60 mt-2">Loading players...</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleOpenAdd('')}
                        disabled={getAllSelectedPlayers().length >= 11}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Players ({getAllSelectedPlayers().length}/11)
                      </button>
                    </div>
                    {getAllSelectedPlayers().length === 0 ? (
                      <div className="rounded-lg border border-white/20 bg-white/5 px-4 py-6 text-center text-white/60">
                        No players added yet. Use "Add Players" to build your team.
                      </div>
                    ) : (
                      getAllSelectedPlayers()
                        .slice()
                        .sort((a: any, b: any) => getRoleOrderForSort(a.role) - getRoleOrderForSort(b.role))
                        .map((player: any) => (
                          <div
                            key={player.tournament_player_id ?? player.id}
                            className="border border-white/20 rounded-lg p-3 flex items-center justify-between bg-white/5"
                          >
                            <div className="min-w-0">
                              <div className="text-white font-semibold flex items-center gap-2">
                                <span className="shrink-0">{getRoleIcon(player.role)}</span>
                                <span className="truncate">{player.name}</span>
                                <span className="text-xs text-white/60">{player.role}</span>
                              </div>
                              <div className="text-sm text-white/70 truncate">
                                {player.team} • {Number(player.credits || 0).toFixed(1)} cr
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDropPlayer(player)}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold"
                              title="Remove"
                            >
                              −
                            </button>
                          </div>
                        ))
                    )}
                  </div>
                )}

                {/* Captain & Vice-Captain Selection */}
                <div className="mt-6 p-4 bg-white/5 rounded-lg">
                  <h4 className="text-white font-medium mb-3">Captain & Vice-Captain</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={getAllSelectedPlayers().find(p => p.is_captain)?.id || ''}
                      onChange={(e) => {
                        const captainId = parseInt(e.target.value);
                        setTeamSlots(prev => {
                          const updated = { ...prev };
                          Object.keys(updated).forEach(slotId => {
                            if (updated[slotId].player) {
                              updated[slotId].player = {
                                ...updated[slotId].player,
                                is_captain: updated[slotId].player.id === captainId,
                                is_vice_captain: updated[slotId].player.id === captainId ? false : updated[slotId].player.is_vice_captain
                              };
                            }
                          });
                          return updated;
                        });
                      }}
                      className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">Select Captain</option>
                      {getAllSelectedPlayers().map(player => (
                        <option key={player.id} value={player.id} className="text-black">
                          {player.name} ({player.role})
                        </option>
                      ))}
                    </select>
                    <select
                      value={getAllSelectedPlayers().find(p => p.is_vice_captain)?.id || ''}
                      onChange={(e) => {
                        const vcId = parseInt(e.target.value);
                        setTeamSlots(prev => {
                          const updated = { ...prev };
                          Object.keys(updated).forEach(slotId => {
                            if (updated[slotId].player) {
                              updated[slotId].player = {
                                ...updated[slotId].player,
                                is_vice_captain: updated[slotId].player.id === vcId,
                                is_captain: updated[slotId].player.id === vcId ? false : updated[slotId].player.is_captain
                              };
                            }
                          });
                          return updated;
                        });
                      }}
                      className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">Select Vice-Captain</option>
                      {getAllSelectedPlayers().map(player => (
                        <option key={player.id} value={player.id} className="text-black">
                          {player.name} ({player.role})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Save Button */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleTeamBuilderSubmit}
                    disabled={isLoadingTeamBuilder || getAllSelectedPlayers().length !== 11 || !teamBuilderName.trim()}
                    className={`px-6 py-3 rounded-lg font-semibold text-lg transition-all ${
                      isLoadingTeamBuilder || getAllSelectedPlayers().length !== 11 || !teamBuilderName.trim()
                        ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isLoadingTeamBuilder ? 'Creating Team...' : 'Save Team'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        ),
        document.body
      )}

      {/* Player picker modal - same as H2H (filters, sort, scrollable list). Kept mounted when in transfer/team-builder so filter state persists. */}
      {(showTeamBuilder || isTransferMode) && (() => {
        const slotTypeToRole: Record<string, string | null> = {
          wk: 'WK-Batsman', batsman: 'Batsman', bowler: 'Bowler', 'all-rounder': 'All-Rounder', flex: null
        };
        let pickerTitle = 'Select Player';
        let excludePlayerIds: (number | string)[] = [];
        let fixedRole: string | null = null;
        if (selectedRoleForAdd !== null) {
          pickerTitle = `Add player (${selectedRoleForAdd})`;
          fixedRole = selectedRoleForAdd;
          const filled = (transferModeTeam || []).filter((p): p is TeamPlayer => p != null);
          excludePlayerIds = filled.map(p => p.tournament_player_id ?? p.id);
        } else if (selectedPlayerToReplace) {
          pickerTitle = `Replace ${selectedPlayerToReplace?.name ?? 'player'}`;
          const outId = Number(selectedPlayerToReplace.tournament_player_id ?? selectedPlayerToReplace.id);
          excludePlayerIds = (userTeam?.players || [])
            .map((p: any) => p.tournament_player_id ?? p.id)
            .filter((id: any) => Number(id) !== outId);
          const isFlex = selectedPlayerToReplace.isFlexPlayer ?? false;
          fixedRole = isFlex ? null : (selectedPlayerToReplace.role || null);
        } else if (selectedSlotId && teamSlots[selectedSlotId]) {
          const slot = teamSlots[selectedSlotId];
          pickerTitle = `Select ${getSlotLabel(slot.type, 0)}`;
          fixedRole = slotTypeToRole[slot.type] ?? null;
          excludePlayerIds = Object.entries(teamSlots)
            .filter(([id, s]) => id !== selectedSlotId && s.player)
            .map(([, s]) => (s.player as any).tournament_player_id ?? (s.player as any).id);
        } else if (isTransferMode && transferModeTeam && (transferModeTeam.filter((p): p is TeamPlayer => p != null).length === 11)) {
          const filled = transferModeTeam.filter((p): p is TeamPlayer => p != null);
          excludePlayerIds = filled.map(p => p.tournament_player_id ?? p.id);
        }
        const isTeamBuilderAddMode = showTeamBuilder && !isTransferMode;
        const isAddMode = isTeamBuilderAddMode || selectedRoleForAdd !== null || (isTransferMode && (transferModeTeam?.length ?? 0) === 11);
        const finalTeamPlayers = isTeamBuilderAddMode
          ? getAllSelectedPlayers()
          : isAddMode
            ? (transferModeTeam || []).filter((p): p is TeamPlayer => p != null)
            : (userTeam?.players || []);
        const batchAddTitle = selectedRoleForAdd
          ? `Add ${selectedRoleForAdd} (${finalTeamPlayers.length}/11)`
          : `Add players (${finalTeamPlayers.length}/11)`;
        const modalTitle = isAddMode ? batchAddTitle : pickerTitle;
        return (
          <PlayerPickerModal
            isOpen={showPlayerSelection}
            onBack={() => {
              setShowPlayerSelection(false);
              setSelectedSlotId(null);
              setSelectedRoleForAdd(null);
              setSelectedPlayerToReplace(null);
            }}
            title={modalTitle}
            players={teamBuilderPlayers || []}
            excludePlayerIds={excludePlayerIds}
            fixedRole={fixedRole}
            teamOptions={getUniqueTeams()}
            matchOptions={getAvailableMatches().map((m: any) => ({
              id: m.id?.toString?.() ?? String(m.id),
              name: formatMatchName(m.match_name || m.name || `${m.team1 || 'TBD'} vs ${m.team2 || 'TBD'}`),
              team1: m.team1,
              team2: m.team2
            }))}
            foreignPlayerIds={budgetLeagueForeignPlayerIds || []}
            disableBodyScrollLock={true}
            rowAction={isAddMode ? 'add_button' : 'tap'}
            creditsCap={100}
            {...(isAddMode
              ? {
                  stagedPicks: finalTeamPlayers.map((p: TeamPlayer) => ({
                    tournament_player_id: Number(p.tournament_player_id ?? p.id),
                    name: p.name,
                    role: p.role,
                    credits: p.credits,
                    team: p.team,
                    player_id: p.player_id
                  })),
                  onRemoveStaged: (tid: number) => {
                    const p = finalTeamPlayers.find(
                      (x: TeamPlayer) => Number(x.tournament_player_id ?? x.id) === Number(tid)
                    );
                    if (p) handleDropPlayer(p);
                  }
                }
              : {})}
            canSelect={(player: any) => {
              const filled = finalTeamPlayers;
              const filledIds = filled.map((p: any) => Number(p.tournament_player_id ?? p.id));
              const isInTeam = filledIds.some((id: number) => id === Number(player.tournament_player_id ?? player.id));
              const currentCredits = isTeamBuilderAddMode
                ? filled.reduce((sum: number, p: any) => sum + (parseFloat(String(p?.credits ?? 0)) || 0), 0)
                : getProjectedBudget();
              const availableCredits = 100 - currentCredits;
              const canAfford = isAddMode ? (parseFloat(player.credits) || 9) <= availableCredits : (selectedPlayerToReplace ? canAddTransfer(player, selectedPlayerToReplace) : true);
              const canAddForComposition = !isAddMode || canAddPlayerForComposition(filled, player.role ?? '');
              const foreignIds = budgetLeagueForeignPlayerIds || [];
              const currentForeignCount = filled.filter((p: any) => foreignIds.includes(String(p?.player_id ?? ''))).length;
              const isPlayerForeign = foreignIds.includes(String(player?.player_id ?? ''));
              const isOutForeign = selectedPlayerToReplace ? foreignIds.includes(String(selectedPlayerToReplace?.player_id ?? '')) : false;
              const canAddUnderForeignLimit = isAddMode
                ? (!isPlayerForeign || currentForeignCount < MAX_FOREIGN_PLAYERS)
                : (currentForeignCount - (isOutForeign ? 1 : 0) + (isPlayerForeign ? 1 : 0)) <= MAX_FOREIGN_PLAYERS;
              return canAfford && !isInTeam && canAddForComposition && canAddUnderForeignLimit;
            }}
            getRowSubtext={(player: any) => {
              const filled = finalTeamPlayers;
              const filledIds = filled.map((p: any) => Number(p.tournament_player_id ?? p.id));
              const isInTeam = filledIds.some((id: number) => id === Number(player.tournament_player_id ?? player.id));
              if (isInTeam) return <span className="text-slate-400">✓ In Team</span>;
              if (selectedPlayerToReplace) {
                const creditChange = (parseFloat(player.credits) || 0) - (parseFloat(selectedPlayerToReplace.credits) || 0);
                if (creditChange !== 0) {
                  return (
                    <span className={creditChange > 0 ? 'text-red-300' : 'text-green-300'}>
                      {creditChange > 0 ? '+' : ''}{creditChange.toFixed(1)} cr
                    </span>
                  );
                }
              }
              const currentCredits = isTeamBuilderAddMode
                ? filled.reduce((sum: number, p: any) => sum + (parseFloat(String(p?.credits ?? 0)) || 0), 0)
                : getProjectedBudget();
              const availableCredits = 100 - currentCredits;
              if (isAddMode && (parseFloat(player.credits) || 9) > availableCredits) {
                return <span className="text-red-400">⚠️ Over budget</span>;
              }
              return null;
            }}
            onSelect={(player: any) => {
              if (isAddMode) {
                handleAddPlayer(player);
              } else {
                handlePlayerSelect(player);
                setShowPlayerSelection(false);
                setSelectedSlotId(null);
                setSelectedRoleForAdd(null);
                setSelectedPlayerToReplace(null);
              }
            }}
          />
        );
      })()}

      {/* Notification */}
      {notification && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {notification}
        </div>
      )}
    </div>
  );
};

export default BudgetLeaguePage; 
